import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Multiplayer.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import AdSenseLayout from '../AdSense/AdSenseLayout';
import WispyStars from '../Tutorials/WispyStars';
import ZodiacEngineService from '../ZodiacEngineService';
import FENHistory from '../../services/zodiacEngine/Fen/FENHistory';
import GameInfo from '../GameInfo/GameInfo';
import TurnPopover from '../TurnPopover/TurnPopover';
import { PieceType, State, Mode } from '../../services/zodiacEngine/types';
import KingSelectionModal from '../KingSelection/KingSelectionModal';
import { Piece } from '../../services/zodiacEngine/Piece';
import { allPieceTypes } from '../../services/zodiacEngine/const';
import { PieceSelectionPanel } from '../shared/PieceSelectionPanel';
import sharedStyles from '../shared/PieceSelection.module.css';
import {
  matchmakingService,
  MatchmakingMode,
  MatchmakingStatus
} from '../../services/matchmaking/MatchmakingService';
import PostGameModal from '../PostGameModal/PostGameModal';
import { GameResult, TimeControlType } from '../../services/MMR/types';
import MMRService from '../../services/MMR/MMRService';
import { UserDataService } from '../../services/firebase/userDataService';
import UserProfileModal from '../UserProfileModal/UserProfileModal';
import RematchRequestModal from '../RematchRequestModal/RematchRequestModal';
import {
  updateChatMessages,
  listenForDocumentChanges,
  sendRematchRequest,
  acceptRematchRequest,
  declineRematchRequest,
  clearRematchRequest,
  resetTimeControlInFirebase,
  getGameStateByGameId,
  sendKingRevealRequest,
  respondToKingRevealRequest,
  queryActiveGamesForUser
} from '../../services/firebase/firebase';
import { convertFromFirebaseState } from '../../services/firebase/firebaseConverter';
import SpectateModal from '../SpectateModal/SpectateModal';
import TimeControlManager from '../../services/timeControl/TimeControlManager';
import { TimeControlPreset, getPresetConfig, isTimedPreset } from '../../services/timeControl';
import TimerDisplay from '../TimeControl/TimerDisplay';
import { getChatEligibility } from '../../utils/ageUtils';
import AuthModal from '../AuthModal/AuthModal';
import { BotGameController } from '../../services/bots/BotGameController';
import { getBotConfig, getBotDefinition, botToVariantId } from '../../services/bots/BotService';
import { AIArenaFirebaseService } from '../../services/AIArena/AIArenaFirebaseService';
import { PieceStatsCollector } from '../../services/bots/pieceStats';
import '../../services/bots/initializeBots'; // Exposes window.__initBots() in dev
import { playUISound, playVictorySound, playDefeatSound } from '../../utils/UISoundEffects';

// Random delay to humanize AI responses (500ms-3000ms)
const botDelay = () => new Promise<void>(resolve =>
  setTimeout(resolve, 500 + Math.random() * 2500)
);

function Multiplayer() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { user, userProfile, updateProfile, accountType } = useContext(AuthContext);
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState,
    setIsSpectating: setContextIsSpectating,
    setGameId: setContextGameId,
    clearAllKingGuesses,
    setBotKingGuess,
    clearBotKingGuess,
    kingHighlightActive,
    activateKingHighlight,
  } = useContext(GameContext);

  const fenHistoryRef = useRef<any>(null);

  // Clock initialization tracking - prevents Firebase sync from corrupting fresh clock values
  const lastInitializedGameIdRef = useRef<string | null>(null);
  const clockInitTimestampRef = useRef<number>(0);
  const CLOCK_INIT_COOLDOWN_MS = 3000; // Don't sync Firebase times for 3 seconds after clock init
  const lastHandledTurnRef = useRef<number>(-1); // Track last turnCount where we locally applied handleTurnChange

  // Check if user is in PRIMARY placement (Marathon) - determines if they see placement UI vs mode selector
  const isInPrimaryPlacement = userProfile && !userProfile.mmr.timeControlRatings?.marathon?.isPlaced;

  // Helper functions for placement status
  const getPlacementStatus = (timeControl: TimeControlType): { isPlaced: boolean; games: number } => {
    const tcRating = userProfile?.mmr.timeControlRatings?.[timeControl];
    return {
      isPlaced: tcRating?.isPlaced || false,
      games: tcRating?.placementGames || 0
    };
  };

  const allPlacementsComplete = (): boolean => {
    return (['sprint', 'standard', 'marathon'] as TimeControlType[]).every(tc =>
      getPlacementStatus(tc).isPlaced
    );
  };

  // Legacy check for backwards compatibility
  const isInPlacement = isInPrimaryPlacement;

  // Matchmaking state
  const [selectedMode, setSelectedMode] = useState<MatchmakingMode>(() => {
    // Load saved mode from localStorage on initial mount
    // Migrate 'quick' to 'competitive' for existing users
    const savedMode = localStorage.getItem('zodiac-matchmaking-mode');
    if (savedMode === 'quick') {
      localStorage.setItem('zodiac-matchmaking-mode', 'competitive');
      return 'competitive';
    }
    if (savedMode && ['competitive', 'casual'].includes(savedMode)) {
      return savedMode as MatchmakingMode;
    }
    return 'competitive';
  });
  const [selectedTimeControl, setSelectedTimeControl] = useState<TimeControlPreset>(() => {
    // Load saved time control from localStorage
    const saved = localStorage.getItem('zodiac-time-control');
    if (saved && Object.values(TimeControlPreset).includes(saved as TimeControlPreset)) {
      return saved as TimeControlPreset;
    }
    return TimeControlPreset.STANDARD;
  });
  // Check if user is in placement for the SELECTED time control (not just Marathon)
  // Used for display in search panel and game info — does NOT affect matchmaking logic
  const isInPlacementForSelectedTC = (): boolean => {
    if (isInPrimaryPlacement) return true;
    if (selectedMode !== 'competitive') return false;
    const tcType: TimeControlType = selectedTimeControl === TimeControlPreset.SPRINT ? 'sprint'
      : selectedTimeControl === TimeControlPreset.MARATHON ? 'marathon'
      : 'standard';
    return !getPlacementStatus(tcType).isPlaced;
  };

  const [searchStatus, setSearchStatus] = useState<MatchmakingStatus>({
    searching: false,
    playersInQueue: 0
  });
  const [gameId, setGameId] = useState<string | null>(null);
  const [matchFound, setMatchFound] = useState(false);

  // Game state
  const [gamePhase, setGamePhase] = useState<'matchmaking' | 'king-selection' | 'piece-placement' | 'playing'>('matchmaking');
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [selectedLightKing, setSelectedLightKing] = useState<PieceType | null>(null);
  const [selectedDarkKing, setSelectedDarkKing] = useState<PieceType | null>(null);
  const [isSelectingForLight, setIsSelectingForLight] = useState(true);
  const [placedPieces, setPlacedPieces] = useState<Set<string>>(new Set());
  const [isLightPieceTurn, setIsLightPieceTurn] = useState(true);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const [isWaitingForOpponent, setIsWaitingForOpponent] = useState(false);
  const [waitingMessage, setWaitingMessage] = useState<string>('');

  // Private game state
  const [privateGameId, setPrivateGameId] = useState<string | null>(null);
  const [joinGameId, setJoinGameId] = useState('');
  const [copied, setCopied] = useState(false);
  const [isCreatingGame, setIsCreatingGame] = useState(false);
  const [isJoiningGame, setIsJoiningGame] = useState(false);
  const [privateTimeControl, setPrivateTimeControl] = useState<TimeControlPreset>(TimeControlPreset.STANDARD);

  // Spectate state
  const [showSpectateModal, setShowSpectateModal] = useState(false);
  const [isSpectating, setIsSpectating] = useState(false);
  const [spectatingGameId, setSpectatingGameId] = useState<string | null>(null);

  // Matchmaking tab toggle (Find Match vs Private Game)
  const [matchmakingTab, setMatchmakingTab] = useState<'findMatch' | 'privateGame'>('findMatch');

  // Chat state
  const [chatMessages, setChatMessages] = useState<Array<{username: string, text: string, timestamp: number}>>([]);
  const [chatInput, setChatInput] = useState('');
  const [isSendingChat, setIsSendingChat] = useState(false);
  const [opponentTriedToChat, setOpponentTriedToChat] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Opponent profile state
  const [opponentProfile, setOpponentProfile] = useState<any>(null);
  const [opponentUsername, setOpponentUsername] = useState<string>('Unknown');

  // Post-game modal state
  const [showPostGameModal, setShowPostGameModal] = useState(false);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [winCondition, setWinCondition] = useState<'kingslayer' | 'race' | 'timeout' | 'surrender' | 'forfeit' | 'draw' | 'threefold' | 'no_moves' | undefined>(undefined);

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Rematch state
  const [showRematchRequest, setShowRematchRequest] = useState(false);
  const [rematchRequestFrom, setRematchRequestFrom] = useState<string | null>(null);
  const [savedMatchmakingMode, setSavedMatchmakingMode] = useState<MatchmakingMode>('competitive');

  // King reveal request state (post-game)
  const [iHaveRequestedReveal, setIHaveRequestedReveal] = useState(false);
  const [opponentRequestedReveal, setOpponentRequestedReveal] = useState(false);
  const [myRequestResponse, setMyRequestResponse] = useState<'accepted' | 'declined' | null>(null);
  const [opponentRequestResponse, setOpponentRequestResponse] = useState<'accepted' | 'declined' | null>(null);

  // Mobile-specific state
  const [isMobileGamePage, setIsMobileGamePage] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPieceForPlacement, setSelectedPieceForPlacement] = useState<{type: PieceType, isLight: boolean} | null>(null);

  // Opponent disconnect detection state
  const [opponentDisconnected, setOpponentDisconnected] = useState(false);
  const [showDisconnectNotice, setShowDisconnectNotice] = useState(false);
  const DISCONNECT_FORFEIT_SECONDS = 120; // 120 seconds to reconnect before forfeit
  const DISCONNECT_NOTICE_DELAY_MS = 10000; // 10 seconds before showing disconnect notice
  const [disconnectCountdown, setDisconnectCountdown] = useState(DISCONNECT_FORFEIT_SECONDS);

  // Reconnect state - for players who disconnected and are returning
  const [activeGameToReconnect, setActiveGameToReconnect] = useState<{ id: string; data: any } | null>(null);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);

  // Bot game state
  const botControllerRef = useRef<BotGameController | null>(null);

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-switch to mobile game page when game phase changes to piece-placement or playing
  useEffect(() => {
    if (isMobile && (gamePhase === 'piece-placement' || gamePhase === 'playing')) {
      setIsMobileGamePage(true);
    }
  }, [isMobile, gamePhase]);

  // Cleanup matchmaking on unmount
  useEffect(() => {
    return () => {
      if (searchStatus.searching) {
        matchmakingService.cancelSearch();
      }
    };
  }, [searchStatus.searching]);

  // Player presence - HYBRID approach:
  // 1. Visibility API for tab switches (immediate detection)
  // 2. Heartbeat for tab closes (since beforeunload can't reliably send data)
  // Disconnect triggers if: tab is hidden OR heartbeat stopped for 45 seconds
  const HEARTBEAT_TIMEOUT_MS = 45000; // 45 seconds without heartbeat = disconnected

  useEffect(() => {
    const isActiveGame = gamePhase === 'king-selection' || gamePhase === 'piece-placement' || gamePhase === 'playing';
    if (!isActiveGame || !user || showPostGameModal) return;

    const updatePresence = async (isVisible: boolean) => {
      const eventManager = ZodiacEngineService.zodiacEngine.getEventManager();
      const currentGameId = eventManager.getGameId();
      const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const isLightPlayer = currentGameState.lightPlayer === user.uid;
      const timestamp = Date.now();

      if (!currentGameId) {
        return;
      }

      // Log the gameId so disconnected player can verify they're looking for the right game

      // Directly update Firebase with presence fields
      // NOTE: zodiacEngine.getGameState() returns a COPY, so modifying it doesn't
      // affect the real gameState that sendUpdateToRemote uses. We must update
      // Firebase directly for presence data.
      try {
        const { updateGamesDocument } = await import('../../services/firebase/firebase');
        const presenceUpdate = isLightPlayer
          ? { lightPlayerTabHidden: !isVisible, lightPlayerLastSeen: timestamp }
          : { darkPlayerTabHidden: !isVisible, darkPlayerLastSeen: timestamp };

        await updateGamesDocument(currentGameId, presenceUpdate);
      } catch (error) {
      }
    };

    // Handle visibility change (tab switch)
    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      updatePresence(isVisible);
    };

    // Set initial state as visible and start heartbeat
    updatePresence(true);

    // Send heartbeat every 15 seconds (also updates lastSeen)
    const heartbeatInterval = setInterval(() => {
      // Only send heartbeat if tab is visible
      if (document.visibilityState === 'visible') {
        updatePresence(true);
      }
    }, 15000);

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(heartbeatInterval);
      // Mark as hidden when unmounting (leaving the game component)
      updatePresence(false);
    };
  }, [gamePhase, user, showPostGameModal]);

  // Opponent disconnect detection - checks BOTH tab hidden AND heartbeat
  // Disconnect triggers if: tab is hidden OR heartbeat stopped for 45 seconds
  // Use a ref for monitoring start time so it persists across re-renders
  const monitoringStartTimeRef = useRef<number>(0);
  const prevTurnCountRef = useRef<number>(0);

  useEffect(() => {
    const isActiveGame = gamePhase === 'king-selection' || gamePhase === 'piece-placement' || gamePhase === 'playing';
    if (!isActiveGame || !user || showPostGameModal || gameState.isBotGame) return;

    // Set monitoring start time only once when effect first runs
    if (monitoringStartTimeRef.current === 0) {
      monitoringStartTimeRef.current = Date.now();
    }

    const GRACE_PERIOD_MS = 20000; // 20 seconds grace period for opponent to connect

    const checkOpponentPresence = () => {
      // Use gameState from context (which gets Firebase updates) instead of engine directly
      const isLightPlayer = gameState.lightPlayer === user.uid;

      // Check tab hidden flag
      const opponentTabHidden = isLightPlayer
        ? gameState.darkPlayerTabHidden
        : gameState.lightPlayerTabHidden;

      // Check heartbeat timestamp
      const opponentLastSeen = isLightPlayer
        ? gameState.darkPlayerLastSeen
        : gameState.lightPlayerLastSeen;

      const timeSinceMonitoringStart = Date.now() - monitoringStartTimeRef.current;
      const pastGracePeriod = timeSinceMonitoringStart > GRACE_PERIOD_MS;

      // Heartbeat expired if: we have a lastSeen AND it's been too long
      // OR: we're past grace period AND never received a heartbeat
      const heartbeatExpired = opponentLastSeen
        ? (Date.now() - opponentLastSeen) > HEARTBEAT_TIMEOUT_MS
        : pastGracePeriod; // If no heartbeat after grace period, consider disconnected

      // Disconnect if EITHER tab is hidden OR heartbeat expired
      const isNowDisconnected = opponentTabHidden === true || heartbeatExpired;

      // Debug logging

      if (isNowDisconnected !== opponentDisconnected) {
        setOpponentDisconnected(isNowDisconnected);
        if (isNowDisconnected) {
          const reason = opponentTabHidden === true ? 'tab hidden' : 'heartbeat expired';
        } else {
        }
      }
    };

    // Check every 5 seconds
    const intervalId = setInterval(checkOpponentPresence, 5000);

    // Also check immediately
    checkOpponentPresence();

    return () => clearInterval(intervalId);
  }, [gamePhase, user, showPostGameModal, opponentDisconnected, gameState, HEARTBEAT_TIMEOUT_MS]);

  // Reset disconnect state when game phase changes to non-active
  useEffect(() => {
    if (gamePhase !== 'king-selection' && gamePhase !== 'playing' && gamePhase !== 'piece-placement') {
      setOpponentDisconnected(false);
      setShowDisconnectNotice(false);
      setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);
      monitoringStartTimeRef.current = 0; // Reset for next game
    }
    // Reset reconnect check when entering a game, so user can check again after game ends
    if (gamePhase === 'king-selection' || gamePhase === 'playing' || gamePhase === 'piece-placement') {
      reconnectCheckCompleteRef.current = false;
    }
  }, [gamePhase]);

  // Delay showing the disconnect notice by 10 seconds to avoid flashing on brief hiccups
  useEffect(() => {
    if (!opponentDisconnected) {
      setShowDisconnectNotice(false);
      return;
    }

    const delayTimer = setTimeout(() => {
      setShowDisconnectNotice(true);
    }, DISCONNECT_NOTICE_DELAY_MS);

    return () => clearTimeout(delayTimer);
  }, [opponentDisconnected]);

  // Disconnect countdown timer - only starts after the notice is shown (10s buffer not counted)
  useEffect(() => {
    if (!showDisconnectNotice) {
      // Reset countdown when opponent reconnects or notice is hidden
      setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);
      return;
    }

    // Start countdown only once the notice is visible
    const countdownInterval = setInterval(() => {
      setDisconnectCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [showDisconnectNotice]);

  // Immediately clear disconnect state when opponent makes a move.
  // If they can submit a move, they're clearly connected — no need to wait for heartbeat.
  useEffect(() => {
    const turnJustChanged = gameState.turnCount !== prevTurnCountRef.current;
    prevTurnCountRef.current = gameState.turnCount;

    if (!turnJustChanged || !opponentDisconnected) return;
    if (gamePhase !== 'playing' || !user) return;

    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isNowOurTurn = gameState.isLightTurn === isLightPlayer;

    // If it just became our turn, the opponent made the move — they're back
    if (isNowOurTurn) {
      setOpponentDisconnected(false);
      setShowDisconnectNotice(false);
    }
  }, [gameState.turnCount, gameState.isLightTurn, opponentDisconnected, gamePhase, user, gameState.lightPlayer]);

  // Handle countdown expiry - mark game as forfeit/cancelled in Firebase for BOTH casual and ranked
  useEffect(() => {
    if (disconnectCountdown === 0 && opponentDisconnected) {
      const handleForfeit = async () => {
        const isCasualGame = selectedMode === 'casual';
        const isSetupPhase = gamePhase === 'king-selection';

        // During king-selection, just cancel (no winner) and auto-return
        if (isSetupPhase) {
          await handleCancelGame();
          return;
        }

        // CRITICAL: Mark game as forfeit in Firebase FIRST
        // This prevents reconnect modal from showing for either player
        const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
        const isLightPlayer = currentGameState.lightPlayer === user?.uid;

        // Mark the game as ended - the player who stayed wins
        try {
          const { updateGamesDocument } = await import('../../services/firebase/firebase');
          const currentGameId = ZodiacEngineService.zodiacEngine.getGameId();
          if (currentGameId) {
            await updateGamesDocument(currentGameId, {
              lightWon: isLightPlayer, // Player who stayed wins
              state: 'PostGame',
              winCondition: 'forfeit'
            });

            // Add to dismissed list so we don't see reconnect modal even after page reload
            addDismissedGameId(currentGameId);
          }

          // Clean up Firebase listener
          const eventManager = (ZodiacEngineService.zodiacEngine as any).eventManager;
          if (eventManager?.unsubscribeDocument) {
            eventManager.unsubscribeDocument();
            eventManager.unsubscribeDocument = undefined;
          }
          if (eventManager) {
            eventManager.gameId = undefined;
          }
        } catch (error) {
        }

        // Clear local game ID to prevent reconnect check from finding this game
        setGameId(null);
        setContextGameId(null);

        // Mark that we just handled a forfeit as the staying player
        // This prevents the reconnect modal from showing
        justHandledForfeitRef.current = true;

        if (!isCasualGame) {
          // Ranked/Placement: Auto-return to multiplayer lobby
          setGamePhase('matchmaking');
          setOpponentDisconnected(false);
          setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);

          // Clear the board and reset
          const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
          gameStateManager.emptyBoard();
          gameStateManager.setMode(Mode.Live);
          gameStateManager.setGameStateState(State.KingSelection);
          const freshGameState = gameStateManager.getGameState();
          freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
          freshGameState.lightKing = null;
          freshGameState.darkKing = null;
          freshGameState.lightWon = undefined;
          freshGameState.isLightTurn = true;
          freshGameState.lightPlayer = undefined;
          freshGameState.darkPlayer = undefined;
          updateGameState(freshGameState);
        }
        // For casual: just show the "Search Again" button, don't auto-redirect
      };

      handleForfeit();
    }
  }, [disconnectCountdown, opponentDisconnected, selectedMode, updateGameState, user, setContextGameId, gamePhase]);

  // Save selected mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('zodiac-matchmaking-mode', selectedMode);
  }, [selectedMode]);

  // Clear board and initialize game mode when component mounts to prevent stale pieces
  // CRITICAL: Also unsubscribe from any existing Firebase listeners to prevent auto-loading
  // old game state. This ensures the reconnect modal can show before the game loads.
  useEffect(() => {

    // Unsubscribe from any existing Firebase listener to prevent auto-loading
    // This is crucial for the reconnect modal to work - without this, the old listener
    // would load the game state before we can show the reconnect choice
    const eventManager = (ZodiacEngineService.zodiacEngine as any).eventManager;
    if (eventManager?.unsubscribeDocument) {
      eventManager.unsubscribeDocument();
      eventManager.unsubscribeDocument = undefined;
    }
    // Clear the old gameId so we don't auto-reconnect
    if (eventManager?.gameId) {
      eventManager.gameId = undefined;
    }

    const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
    gameStateManager.emptyBoard();
    gameStateManager.setMode(Mode.Live);
    gameStateManager.setGameStateState(State.KingSelection);

    // Reset game state properties
    const freshGameState = gameStateManager.getGameState();
    freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
    (freshGameState as any).repeatString = "";
    freshGameState.lightKing = null;
    freshGameState.darkKing = null;
    freshGameState.lightWon = undefined;
    freshGameState.isLightTurn = true;

    // Update the context
    updateGameState(freshGameState);
  }, []);

  // Initialize MMR profile when component mounts
  useEffect(() => {
    const initializeMMR = async () => {
      if (!user || !userProfile) return;


      const needsInitialization = !userProfile.mmr ||
                                    userProfile.mmr.provisionalRating === undefined ||
                                    userProfile.mmr.placementGames === undefined ||
                                    userProfile.mmr.colorBalance === undefined;

      if (needsInitialization) {
        try {
          const initializedProfile = await MMRService.initializeUserMMR(user);
          await updateProfile(initializedProfile);
        } catch (error) {
        }
      } else {
      }
    };

    initializeMMR();
  }, [user, userProfile, updateProfile]);

  // Track if we just handled a forfeit (player who stayed) to prevent reconnect modal
  const justHandledForfeitRef = useRef<boolean>(false);

  // Track dismissed game IDs - persisted to localStorage so survives page reloads
  // This is critical for the staying player who shouldn't see reconnect modal after forfeit
  const DISMISSED_GAMES_KEY = 'zodiac-dismissed-reconnect-games';
  const dismissedGameIdsRef = useRef<Set<string>>(new Set());

  // Load dismissed games from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(DISMISSED_GAMES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Clean up old entries (older than 1 hour)
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        const validEntries = parsed.filter((entry: { id: string; timestamp: number }) =>
          entry.timestamp > oneHourAgo
        );
        validEntries.forEach((e: { id: string }) => dismissedGameIdsRef.current.add(e.id));
      }
    } catch (e) {
    }
  }, []);

  // Helper to add a game ID to dismissed set and persist to localStorage
  const addDismissedGameId = (gameId: string) => {
    dismissedGameIdsRef.current.add(gameId);
    try {
      const stored = localStorage.getItem(DISMISSED_GAMES_KEY);
      const existing = stored ? JSON.parse(stored) : [];
      // Add new entry with timestamp
      existing.push({ id: gameId, timestamp: Date.now() });
      // Keep only last 50 entries to prevent unbounded growth
      const trimmed = existing.slice(-50);
      localStorage.setItem(DISMISSED_GAMES_KEY, JSON.stringify(trimmed));
    } catch (e) {
    }
  };

  // Track if user has clicked "Forfeit Game" - stops ALL further reconnect checks this session
  const userForfeitedAllGamesRef = useRef<boolean>(false);

  // Track if initial reconnect check is complete (no games found) - stops further polling
  const reconnectCheckCompleteRef = useRef<boolean>(false);

  // Check for active games to reconnect to when entering matchmaking
  // Only checks ONCE - if no games found, stops. No need to poll since grace period is 60s.
  useEffect(() => {
    // Don't run if not in matchmaking, already showing modal, or in a bot game
    if (!user || gamePhase !== 'matchmaking' || showReconnectModal || isReconnecting || gameId || gameState.isBotGame) {
      return;
    }

    // If user already clicked "Forfeit Game" this session, stop all reconnect checks
    if (userForfeitedAllGamesRef.current) {
      return;
    }

    // If we already checked and found no games, don't check again (saves Firebase reads)
    if (reconnectCheckCompleteRef.current) {
      return;
    }

    // If we just handled a forfeit (as the player who stayed), skip checks for this cycle
    if (justHandledForfeitRef.current) {
      justHandledForfeitRef.current = false;
      return;
    }

    const checkForActiveGame = async () => {
      // Re-check conditions inside async function in case state changed
      if (showReconnectModal || isReconnecting || userForfeitedAllGamesRef.current || reconnectCheckCompleteRef.current) return;

      try {
        const activeGames = await queryActiveGamesForUser(user.uid);
        console.log('[FRIEND-GAME] Reconnect check found', activeGames.length, 'active games', activeGames.map(g => g.id));

        if (activeGames.length > 0) {
          // Filter out games we've already dismissed this session
          const nonDismissedGames = activeGames.filter(g => !dismissedGameIdsRef.current.has(g.id));

          if (nonDismissedGames.length > 0) {
            const gameToReconnect = nonDismissedGames[0];
            setActiveGameToReconnect(gameToReconnect);
            setShowReconnectModal(true);
          } else {
            // All games were dismissed - no point checking again
            reconnectCheckCompleteRef.current = true;
          }
        } else {
          // No active games found - no point checking again (grace period is 60s, if nothing now, nothing later)
          reconnectCheckCompleteRef.current = true;
        }
      } catch (error) {
        // On error, also stop checking to prevent infinite error loops
        reconnectCheckCompleteRef.current = true;
      }
    };

    // Check once when entering matchmaking - no need to poll
    checkForActiveGame();
  }, [user, gamePhase, showReconnectModal, isReconnecting, gameId]);

  // Auto-join game from ?join= URL parameter (used by friend game invites)
  const joinFromUrlProcessedRef = useRef(false);
  useEffect(() => {
    if (!user || joinFromUrlProcessedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const joinId = params.get('join');
    if (!joinId) return;

    joinFromUrlProcessedRef.current = true;
    // Clear the URL param to prevent re-triggering
    window.history.replaceState({}, document.title, window.location.pathname);

    const doJoin = async () => {
      setIsJoiningGame(true);
      console.log('[FRIEND-GAME] Joiner: joining game', joinId, 'as', user.uid);
      try {
        await ZodiacEngineService.zodiacEngine.joinGame(joinId, user.uid, true);
        clearAllKingGuesses();
        setGameId(joinId);
        setContextGameId(joinId);
        const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
        console.log('[FRIEND-GAME] Joiner: joined successfully', { lightPlayer: newGameState.lightPlayer, darkPlayer: newGameState.darkPlayer, state: newGameState.state });

        // Sync matchmaking mode from the game document the host created
        if (newGameState.matchmakingMode === 'casual') {
          setSavedMatchmakingMode('casual');
        }

        updateGameState(newGameState);
        setGamePhase('king-selection');
      } catch (err) {
        console.error('[FRIEND-GAME] Joiner: join failed', err);
        alert('Failed to join game. The invite may have expired.');
      } finally {
        setIsJoiningGame(false);
      }
    };
    doJoin();
  }, [user]);

  // Host arriving after friend accepted invite via ?host= URL parameter
  const hostFromUrlProcessedRef = useRef(false);
  useEffect(() => {
    if (!user || hostFromUrlProcessedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const hostGameId = params.get('host');
    if (!hostGameId) return;

    hostFromUrlProcessedRef.current = true;
    window.history.replaceState({}, document.title, window.location.pathname);

    const doHost = async () => {
      try {
        // Reconnect to the game we created (engine detects we're already lightPlayer)
        await ZodiacEngineService.zodiacEngine.joinGame(hostGameId, user.uid, false);
        ZodiacEngineService.zodiacEngine.setMatchmakingMode('casual');
        const engineState = ZodiacEngineService.zodiacEngine.getGameState();
        engineState.timeControlPreset = 'marathon';
        engineState.lightPlayerMatchmakingMode = 'casual';
        engineState.darkPlayerMatchmakingMode = 'casual';
        clearAllKingGuesses();
        setGameId(hostGameId);
        setContextGameId(hostGameId);
        setSavedMatchmakingMode('casual');
        updateGameState(engineState);
        setGamePhase('king-selection');
      } catch (err) {
        console.error('[FRIEND-GAME] Host reconnect failed:', err);
      }
    };
    doHost();
  }, [user]);

  // Track if we've already processed this game to prevent duplicates
  const [processedGameId, setProcessedGameId] = useState<string | null>(null);

  // Detect game end and show post-game modal
  useEffect(() => {
    if (!user || !userProfile) return;
    if (gamePhase !== 'playing') return;
    if (gameState.state !== State.PostGame && gameState.state !== State.Draw) return;

    // Prevent duplicate processing of the same game
    if (processedGameId === gameId) {
      return;
    }


    // Stop the timer immediately when game ends
    TimeControlManager.stopOnGameEnd();

    // Determine winner
    const isLightPlayer = gameState.lightPlayer === user.uid;
    let winner: 'light' | 'dark' | 'draw' = 'draw';

    if (gameState.state === State.PostGame) {
      winner = gameState.lightWon === true ? 'light' : 'dark';
    }

    // Same ending cues as the AI game (see UISoundEffects): rising for a win,
    // falling for a loss, the plain tick for a draw.
    if (winner === 'draw') playUISound();
    else if ((winner === 'light') === isLightPlayer) playVictorySound();
    else playDefeatSound();

    // Process MMR and get real game result
    const processMMR = async () => {
      // Read fresh state directly from engine (React closure gameState may be stale)
      const currentEngineState = ZodiacEngineService.zodiacEngine.getGameState();
      const isBotGame = currentEngineState.isBotGame || gameState.isBotGame;


      // For bot games: write final game record to Firebase FIRST (before MMR which may fail for bots)
      // MMRService.processGameResult throws when fetching bot's non-existent user profile,
      // so this must run before MMR to ensure the game record is always saved.
      if (isBotGame && gameId) {
        try {
          let botGameWinCondition = (currentEngineState as any).winCondition || '';
          if (!botGameWinCondition) {
            botGameWinCondition = currentEngineState.state === State.Draw ? 'draw' : 'race';
          }

          const { updateGamesDocument } = await import('../../services/firebase/firebase');
          await updateGamesDocument(gameId, {
            state: currentEngineState.state === State.PostGame ? 'PostGame' : 'Draw',
            lightWon: currentEngineState.state === State.Draw ? 'draw' : currentEngineState.lightWon,
            moveHistory: currentEngineState.fenHistory,
            winCondition: botGameWinCondition,
            turnCount: currentEngineState.turnCount,
            completedAt: Date.now(),
          });
        } catch (fbError) {
        }
      }

      // Record piece stats for AI learning (non-fatal, runs for all bot games)
      if (isBotGame && currentEngineState.botAiModel) {
        try {
          // botAiModel is a string matching AIModel enum values (e.g. "sentinel", "gaia")
          const botIsLight = currentEngineState.botPlayerId === currentEngineState.lightPlayer;
          const botWon = currentEngineState.state === State.Draw ? false
            : (currentEngineState.lightWon === true) === botIsLight;

          await PieceStatsCollector.recordGameResult(
            currentEngineState.botAiModel as any,
            botIsLight,
            currentEngineState,
            botWon
          );
        } catch (statsError) {
        }
      }

      try {

        if (!gameState.lightPlayer || !gameState.darkPlayer || !gameId) {
          return;
        }

        // Quick mode removed - now only Competitive and Casual
        const gameTimeControl = gameState.timeControlPreset || 'standard';

        // CRITICAL FIX: Use savedMatchmakingMode for THIS player's mode
        // The gameState per-player modes may not be synced correctly from Firebase in cross-mode matches
        // savedMatchmakingMode is the authoritative source for what mode THIS player selected
        const myMode = savedMatchmakingMode;
        const lightPlayerMode = isLightPlayer
          ? myMode
          : (gameState.lightPlayerMatchmakingMode || gameState.matchmakingMode);
        const darkPlayerMode = isLightPlayer
          ? (gameState.darkPlayerMatchmakingMode || gameState.matchmakingMode)
          : myMode;


        const { lightPlayerResult, darkPlayerResult} = await MMRService.processGameResult(
          gameId,
          gameState.lightPlayer,
          gameState.darkPlayer,
          winner,
          gameState.matchmakingMode, // Pass the matchmaking mode from game state
          gameTimeControl,  // Time control preset
          lightPlayerMode,  // Per-player mode - use savedMatchmakingMode for current player
          darkPlayerMode    // Per-player mode - use savedMatchmakingMode for current player
        );

        // Sync bot's new ELO to arena ratings (single source of truth)
        if (isBotGame && gameState.botPlayerId) {
          try {
            const botDef = getBotDefinition(gameState.botPlayerId);
            if (botDef) {
              const variantId = botToVariantId(botDef);
              const botResult = isLightPlayer ? darkPlayerResult : lightPlayerResult;
              const botOutcome = botResult.result === 'win' ? 'win' as const
                : botResult.result === 'loss' ? 'loss' as const : 'draw' as const;

              await AIArenaFirebaseService.updateModelRating(
                variantId,
                botDef.username,
                botDef.config.aiModel,
                botDef.config.timeFrame,
                botResult.ratingAfter,
                botResult.ratingChange,
                botOutcome,
                currentEngineState.turnCount || 0,
                0, 0, null
              );
            }
          } catch (error) {
          }
        }

        // Get the result for this specific player
        const thisPlayerResult = isLightPlayer ? lightPlayerResult : darkPlayerResult;

        // Update the user profile locally with the game result
        // (Don't re-fetch from Firebase - there's a race condition with eventual consistency)
        try {
          if (userProfile && thisPlayerResult) {
            // Clone the profile to avoid mutation
            const updatedProfile = JSON.parse(JSON.stringify(userProfile));

            // Determine the time control type
            const timeControlType = gameTimeControl === 'sprint' ? 'sprint'
              : gameTimeControl === 'marathon' ? 'marathon'
              : 'standard';

            // Initialize timeControlRatings if needed
            if (!updatedProfile.mmr.timeControlRatings) {
              updatedProfile.mmr.timeControlRatings = {};
            }
            if (!updatedProfile.mmr.timeControlRatings[timeControlType]) {
              updatedProfile.mmr.timeControlRatings[timeControlType] = {
                rating: 1200,
                provisionalRating: 1200,
                isPlaced: false,
                placementGames: 0,
                games: 0,
                peak: 1200
              };
            }

            // Update the time control rating with the game result
            const tcRating = updatedProfile.mmr.timeControlRatings[timeControlType];
            tcRating.rating = thisPlayerResult.ratingAfter;
            tcRating.provisionalRating = thisPlayerResult.ratingAfter;
            tcRating.games = (tcRating.games || 0) + 1;

            // Update placement status if this was a placement game
            if (thisPlayerResult.gameMode === 'placement') {
              tcRating.placementGames = thisPlayerResult.placementGamesCompleted || (tcRating.placementGames + 1);
              if (tcRating.placementGames >= 10) {
                tcRating.isPlaced = true;
              }

              // Record placement game result in history for W/L/D indicators
              if (!updatedProfile.mmr.placementHistory) {
                updatedProfile.mmr.placementHistory = [];
              }
              updatedProfile.mmr.placementHistory.push({
                opponentRating: thisPlayerResult.opponentRating,
                result: thisPlayerResult.result as 'win' | 'loss' | 'draw',
                ratingChange: thisPlayerResult.ratingAfter - thisPlayerResult.ratingBefore,
                gameDate: new Date(),
                gameNumber: tcRating.placementGames,
                timeControl: timeControlType,
              });
            }

            // Update peak if rating increased
            if (thisPlayerResult.ratingAfter > (tcRating.peak || 0)) {
              tcRating.peak = thisPlayerResult.ratingAfter;
            }

            // Re-sync legacy fields so displayRating = highest placed rating
            MMRService.syncLegacyFields(updatedProfile);

            // Sync fields that processGameResult already saved to Firebase.
            // Without this, updateProfile() overwrites them with stale values from the cloned profile.
            updatedProfile.mmr.colorBalance = (updatedProfile.mmr.colorBalance || 0) + (isLightPlayer ? 1 : -1);
            updatedProfile.mmr.totalGames = (updatedProfile.mmr.totalGames || 0) + 1;
            updatedProfile.mmr.lastGameDate = new Date();

            await updateProfile(updatedProfile);
          }
        } catch (refreshError) {
          // Continue anyway - modal will still show
        }


        // Determine win condition
        let detectedWinCondition: 'kingslayer' | 'race' | 'timeout' | 'surrender' | 'forfeit' | 'draw' | 'threefold' | 'no_moves' | undefined = undefined;
        if (gameState.state === State.Draw) {
          // Check specific draw reason for better UI display
          const drawReason = (gameState as any).drawReason;
          if (drawReason === "3 time repeat draw") {
            detectedWinCondition = 'threefold';
          } else if (drawReason === "No legal moves remain") {
            detectedWinCondition = 'no_moves';
          } else {
            detectedWinCondition = 'draw';
          }
        } else if ((gameState as any).winCondition === 'timeout') {
          detectedWinCondition = 'timeout';
        } else if ((gameState as any).winCondition === 'forfeit') {
          detectedWinCondition = 'forfeit';
        } else {
          // Fallback: check TimeControlManager in case winCondition was lost during state sync
          const timeState = TimeControlManager.getState();
          if (timeState.timeExpired && timeState.winner) {
            detectedWinCondition = 'timeout';
          } else {
            // Check if king was captured (kingslayer) vs race victory
            const winnerIsLight = gameState.lightWon === true;
            const enemyKing = winnerIsLight ? gameState.darkKing : gameState.lightKing;
            const enemyKingStillExists = gameState.board.some(row =>
              row.some(square =>
                square.Pieces.some(piece =>
                  piece.isLight !== winnerIsLight && piece.type === enemyKing
                )
              )
            );

            if (!enemyKingStillExists) {
              detectedWinCondition = 'kingslayer';
            } else {
              // King still exists - must be race victory
              detectedWinCondition = 'race';
            }
          }
        }
        setWinCondition(detectedWinCondition);

        setGameResult(thisPlayerResult);
        setShowPostGameModal(true);
        setProcessedGameId(gameId); // Mark this game as processed

      } catch (error) {

        // Show error message to user
        alert('Failed to process game result. Your rating may not have been updated. Please check your connection and try refreshing the page.');
      }
    };

    processMMR();
  }, [gameState.state, gamePhase, user, userProfile, gameId, gameState.lightWon, gameState.lightPlayer, gameState.darkPlayer, updateProfile]);

  // Detect when someone joins OUR game (we're the host, they're the joiner)
  useEffect(() => {
    console.log('[FRIEND-GAME] Opponent detection effect fired:', {
      searching: searchStatus.searching,
      gameId,
      matchFound,
      lightPlayer: gameState.lightPlayer,
      darkPlayer: gameState.darkPlayer,
      gameState: gameState.state,
      userUid: user?.uid,
    });

    if (!searchStatus.searching && !gameId) {
      console.log('[FRIEND-GAME] Opponent detection: SKIPPED (not searching and no gameId)');
      return;
    }
    if (!user) {
      console.log('[FRIEND-GAME] Opponent detection: SKIPPED (no user)');
      return;
    }
    if (matchFound) {
      console.log('[FRIEND-GAME] Opponent detection: SKIPPED (match already found)');
      return;
    }

    // Check if someone joined our game
    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isDarkPlayer = gameState.darkPlayer === user.uid;
    const hasDarkPlayer = gameState.darkPlayer && gameState.darkPlayer !== '';
    const hasLightPlayer = gameState.lightPlayer && gameState.lightPlayer !== '';

    // Handle both cases: we might be light or dark depending on color balance swap
    const opponentJoined = (isLightPlayer && hasDarkPlayer) || (isDarkPlayer && hasLightPlayer);
    console.log('[FRIEND-GAME] Opponent detection:', { isLightPlayer, isDarkPlayer, hasDarkPlayer, hasLightPlayer, opponentJoined, stateIsKingSelection: gameState.state === State.KingSelection });

    if (opponentJoined && gameState.state === State.KingSelection) {
      console.log('[FRIEND-GAME] HOST: Opponent joined! Transitioning to king-selection');

      // Clear any king guesses from previous games
      clearAllKingGuesses();

      // CRITICAL: Get the gameId from the engine (we created this game, so we need to track its ID)
      const currentGameId = ZodiacEngineService.zodiacEngine.getGameId();
      if (currentGameId && !gameId) {
        setGameId(currentGameId);
        setContextGameId(currentGameId);
      }

      setMatchFound(true);
      setGamePhase('king-selection');

      // Stop searching
      matchmakingService.cancelSearch();
      setSearchStatus({
        searching: false,
        playersInQueue: 0,
        matchFound: true,
        gameId: currentGameId || gameId || undefined,
        weJoined: false
      });
    }
  }, [gameState.darkPlayer, gameState.lightPlayer, gameState.state, searchStatus.searching, user, matchFound, gameId]);

  // Detect when WE joined an opponent's game via Firebase state sync
  useEffect(() => {
    if (!matchFound) return;
    if (!user) return;
    if (gamePhase !== 'matchmaking') return; // Already transitioned

    // Check if we're either light or dark player (color balance can swap roles)
    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isDarkPlayer = gameState.darkPlayer === user.uid;
    const weAreInGame = isLightPlayer || isDarkPlayer;

    if (weAreInGame && gameState.state === State.KingSelection) {
      setGamePhase('king-selection');
    }
  }, [gameState.state, gameState.darkPlayer, gameState.lightPlayer, matchFound, user, gamePhase]);

  // Sync king selections from Firebase and control game phase
  useEffect(() => {
    if (gamePhase !== 'king-selection') return;
    if (!user) return;

    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isDarkPlayer = gameState.darkPlayer === user.uid;
    console.log('[FRIEND-GAME] King selection sync:', { isLightPlayer, isDarkPlayer, lightKing: gameState.lightKing, darkKing: gameState.darkKing, lightPlayer: gameState.lightPlayer, darkPlayer: gameState.darkPlayer });

    // Update local state from Firebase
    if (gameState.lightKing && !selectedLightKing) {
      setSelectedLightKing(gameState.lightKing as PieceType);
    }
    if (gameState.darkKing && !selectedDarkKing) {
      setSelectedDarkKing(gameState.darkKing as PieceType);
    }

    // SIMULTANEOUS SELECTION: Both players can pick at the same time
    const isBotGame = gameState.isBotGame;
    if (isLightPlayer) {
      if (!gameState.lightKing) {
        // Light player needs to select their king
        setIsSelectingForLight(true);
        setIsWaitingForOpponent(false);
      } else if (!gameState.darkKing) {
        // Light player selected, waiting for dark player (bot handles its own selection)
        setIsWaitingForOpponent(!isBotGame);
        if (!isBotGame) setWaitingMessage('Opponent is selecting their king');
      } else {
        // Both kings selected, move to piece placement
        setGamePhase('piece-placement');
        (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.PieceSelection);
        updateGameState(ZodiacEngineService.zodiacEngine.getGameState());
      }
    } else if (isDarkPlayer) {
      if (!gameState.darkKing) {
        // Dark player can select immediately (no waiting!)
        setIsSelectingForLight(false);
        setIsWaitingForOpponent(false);
      } else if (!gameState.lightKing) {
        // Dark player selected, waiting for light player (bot handles its own selection)
        setIsWaitingForOpponent(!isBotGame);
        if (!isBotGame) setWaitingMessage('Opponent is selecting their king');
      } else {
        // Both kings selected, move to piece placement
        setGamePhase('piece-placement');
        (ZodiacEngineService.zodiacEngine as any).gameStateManager.setGameStateState(State.PieceSelection);
        updateGameState(ZodiacEngineService.zodiacEngine.getGameState());
      }
    }
  }, [gameState.lightKing, gameState.darkKing, gamePhase, user, gameState.lightPlayer, gameState.darkPlayer, selectedLightKing, selectedDarkKing, updateGameState]);

  // Sync piece placements from Firebase (detect opponent's placements)
  useEffect(() => {
    if (gamePhase !== 'piece-placement') return;

    // Count pieces on the board from gameState
    const newPlacedPieces = new Set<string>();
    gameState.board.forEach((row, rowIndex) => {
      row.forEach((square) => {
        square.Pieces.forEach((piece) => {
          const pieceKey = `${piece.type}-${piece.isLight ? 'light' : 'dark'}`;
          newPlacedPieces.add(pieceKey);
        });
      });
    });

    setPlacedPieces(newPlacedPieces);

    // Check if all pieces are placed
    const lightPieces = Array.from(newPlacedPieces).filter(key => key.endsWith('-light'));
    const darkPieces = Array.from(newPlacedPieces).filter(key => key.endsWith('-dark'));

    if (lightPieces.length === 5 && darkPieces.length === 5) {

      setGamePhase('playing');
      setIsGameStarted(true);

      // Clean up piece placement entries and initialize with final starting position
      // This gives a clean move history like AI mode: single "Initial position" entry
      const engine = ZodiacEngineService.zodiacEngine as any;
      const currentGameState = engine.gameStateManager.getGameState();
      engine.fenHistoryManager.initializeWithCurrentState(currentGameState);

      // Transition to playing state
      engine.gameStateManager.setGameStateState(State.Playing);

      // Sync the cleaned history to Firebase so both players have consistent state
      // Use moveMade=false to avoid re-adding the current FEN
      engine.eventManager.sendUpdateToRemote(false);

      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(newGameState);

      // Initialize time control
      // Priority order:
      // 1. Placement players always use Marathon (10m +10s) - gives new players more time
      // 2. Placed players use their selected time control (matchmaking ensures compatibility)
      // 3. If user selected ANY, default to Standard
      // Determine effective time control:
      // - Primary placement players always use Marathon (more thinking time for new players)
      // - "Any" preset defaults to Standard
      // - Otherwise use player's selected time control
      let effectiveTimeControl: TimeControlPreset;

      if (isInPrimaryPlacement) {
        // Primary placement players always use Marathon for longer thinking time
        effectiveTimeControl = TimeControlPreset.MARATHON;
      } else if (selectedTimeControl === TimeControlPreset.ANY) {
        // ANY defaults to Standard
        effectiveTimeControl = TimeControlPreset.STANDARD;
      } else {
        effectiveTimeControl = selectedTimeControl;
      }

      const timeConfig = getPresetConfig(effectiveTimeControl);
      if (isInPrimaryPlacement) {
      } else if (selectedTimeControl === TimeControlPreset.ANY) {
      } else {
      }
      TimeControlManager.initialize(timeConfig, 'multiplayer');

      // CRITICAL: Track that we just initialized for this game
      // This prevents Firebase sync from immediately corrupting our fresh clock values
      lastInitializedGameIdRef.current = gameId;
      clockInitTimestampRef.current = Date.now();

      TimeControlManager.startTimer('light'); // Game starts with light

      // If this is a bot game, trigger bot's first move directly
      // (React effects can have timing issues when multiple dependencies change simultaneously)
      if (botControllerRef.current && gameState.isBotGame) {
        setTimeout(() => {
          if (botControllerRef.current) {
            const freshState = ZodiacEngineService.zodiacEngine.getGameState();
            botControllerRef.current.handleTurnChange(freshState);
          }
        }, 1500);
      }
    }
  }, [gameState.board, gamePhase, updateGameState, gameState.isLightTurn]);

  // === BOT GAME EFFECTS ===

  // Initialize bot controller when a bot game is detected
  useEffect(() => {
    if (!gameState.isBotGame || !gameState.botPlayerId || !user) return;
    if (gameState.botPlayerId === user.uid) return; // Safety: user shouldn't be the bot

    // Don't re-initialize if already set up for this bot
    if (botControllerRef.current) return;

    const botConfig = getBotConfig(gameState.botPlayerId);
    if (!botConfig) {
      return;
    }

    const controller = new BotGameController(
      ZodiacEngineService.zodiacEngine,
      gameState.botPlayerId,
      botConfig
    );
    controller.initialize(gameState);
    botControllerRef.current = controller;


    return () => {
      controller.destroy();
      botControllerRef.current = null;
    };
  }, [gameState.isBotGame, gameState.botPlayerId, user]);

  // Bot king selection (handleKingSelection has its own internal delay)
  useEffect(() => {
    if (!botControllerRef.current) return;
    if (gamePhase !== 'king-selection') return;

    // Pass the engine's live state to avoid stale closure issues
    const liveState = ZodiacEngineService.zodiacEngine.getGameState();
    botControllerRef.current.handleKingSelection(liveState);
  }, [gamePhase, gameState.lightKing, gameState.darkKing]);

  // Bot piece placement (handlePiecePlacement has its own internal delay)
  useEffect(() => {
    if (!botControllerRef.current) return;
    if (gamePhase !== 'piece-placement') return;

    const liveState = ZodiacEngineService.zodiacEngine.getGameState();
    botControllerRef.current.handlePiecePlacement(liveState);
  }, [gamePhase, gameState.isLightTurn, gameState.board]);

  // Bot turn handling during gameplay — always pass fresh engine state (not stale React props)
  useEffect(() => {
    if (!botControllerRef.current) return;
    if (gamePhase !== 'playing') return;
    if (gameState.state !== State.Playing) return;

    const freshState = ZodiacEngineService.zodiacEngine.getGameState();
    botControllerRef.current.handleTurnChange(freshState);
  }, [gamePhase, gameState.turnCount, gameState.isLightTurn, gameState.state, gameState.skipTurnChange]);

  // Bot king guess taunt - randomly guess opponent's king after bot's turn
  useEffect(() => {
    if (!gameState.isBotGame || gamePhase !== 'playing' || gameState.state !== State.Playing) return;
    if (gameState.turnCount < 4) return; // Don't guess too early

    const botPlayerId = gameState.botPlayerId;
    if (!botPlayerId) return;
    const botIsLight = gameState.lightPlayer === botPlayerId;
    // Only run when it's the human's turn (bot just finished)
    const isBotsTurn = botIsLight ? gameState.isLightTurn : !gameState.isLightTurn;
    if (isBotsTurn) return;

    const roll = Math.random();
    if (roll < 0.15) {
      // 15% chance: place or move guess to a random opponent piece
      const board = gameState.board;
      const opponentPieces: { piece: any; row: number; col: number }[] = [];
      for (let row = 0; row < board.length; row++) {
        for (let col = 0; col < board[row].length; col++) {
          const pieces = board[row][col]?.Pieces;
          if (pieces && pieces.length > 0) {
            const piece = pieces[0];
            if (piece.isLight !== botIsLight) {
              opponentPieces.push({ piece, row, col });
            }
          }
        }
      }
      if (opponentPieces.length > 0) {
        const pick = opponentPieces[Math.floor(Math.random() * opponentPieces.length)];
        setBotKingGuess({ row: pick.row, col: pick.col }, pick.piece.id, botIsLight);
      }
    } else if (roll < 0.30) {
      // 15% chance: remove existing guess
      clearBotKingGuess(botIsLight);
    }
    // 70% chance: do nothing
  }, [gameState.turnCount]);

  // Bot first move safety net - ensures the bot makes its first move even if the
  // turn handling effect above doesn't fire due to dependency timing issues.
  // When game transitions to playing and it's a bot game, trigger after a short delay.
  useEffect(() => {
    if (!gameState.isBotGame || gamePhase !== 'playing' || gameState.state !== State.Playing) return;
    if (gameState.turnCount > 0) return; // Only for the very first move

    const timer = setTimeout(() => {
      if (!botControllerRef.current) {
        return;
      }
      botControllerRef.current.handleTurnChange(
        ZodiacEngineService.zodiacEngine.getGameState()
      );
    }, 1000);

    return () => clearTimeout(timer);
  }, [gamePhase, gameState.state, gameState.isBotGame, gameState.turnCount]);

  // === END BOT GAME EFFECTS ===

  // Sync chat messages from Firebase - real-time sync (skip for bot games)
  useEffect(() => {
    if (!gameId) return;
    if (gameState.isBotGame) return;
    if (gamePhase !== 'piece-placement' && gamePhase !== 'playing') return;


    // Listen directly to Firebase document for chat updates
    const unsubscribe = listenForDocumentChanges(gameId, (data) => {
      if (data && data.chatMessages) {
        try {
          const parsedMessages = JSON.parse(data.chatMessages);
          setChatMessages(parsedMessages);
        } catch (error) {
        }
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [gameId, gamePhase]);

  // Sync time control from Firebase - real-time sync (skip for bot games — clock is local)
  useEffect(() => {
    if (!gameId) return;
    if (gameState.isBotGame) return;
    if (gamePhase !== 'piece-placement' && gamePhase !== 'playing') return;

    let hasInitialized = false;

    // Listen for time control updates from Firebase
    const unsubscribe = listenForDocumentChanges(gameId, (data) => {
      // Check if we have time control data from Firebase
      if (data && data.timeControlPreset !== undefined) {
        const currentState = TimeControlManager.getState();

        // If TimeControlManager has not been initialized with the correct preset, initialize it
        // This happens when a player joins an existing game
        if (!hasInitialized && currentState.config.preset !== data.timeControlPreset) {

          // Map Firebase preset string to TimeControlPreset enum
          let preset = TimeControlPreset.STANDARD;
          if (data.timeControlPreset === 'sprint') preset = TimeControlPreset.SPRINT;
          else if (data.timeControlPreset === 'standard') preset = TimeControlPreset.STANDARD;
          else if (data.timeControlPreset === 'marathon') preset = TimeControlPreset.MARATHON;
          else if (data.timeControlPreset === 'off') preset = TimeControlPreset.OFF;

          const timeConfig = getPresetConfig(preset);
          TimeControlManager.initialize(timeConfig, 'multiplayer');
          hasInitialized = true;

        }

        // Sync time values if we have them
        // CRITICAL: Skip sync if we JUST initialized - we already have fresh time values
        // Syncing old Firebase values immediately after init caused the "3 seconds in marathon" bug
        // ALSO: Never sync during piece-placement phase - clock hasn't been initialized yet
        if (gamePhase === 'piece-placement') {
        } else if (data.lightTimeRemaining !== undefined && data.darkTimeRemaining !== undefined) {
          // Get the expected initial time for this preset
          const timeConfig = getPresetConfig(
            data.timeControlPreset === 'sprint' ? TimeControlPreset.SPRINT :
            data.timeControlPreset === 'marathon' ? TimeControlPreset.MARATHON :
            data.timeControlPreset === 'off' ? TimeControlPreset.OFF :
            TimeControlPreset.STANDARD
          );
          const initialTime = timeConfig.initialTime;

          // Only sync if:
          // 1. The game has actually started (lastMoveTimestamp exists), AND
          // 2. The time values are reasonable (not stale from a previous game)
          // A time value is considered stale if it's less than 50% of initial AND we haven't moved yet
          const isStaleData = !data.lastMoveTimestamp &&
            (data.lightTimeRemaining < initialTime * 0.5 || data.darkTimeRemaining < initialTime * 0.5);

          // CRITICAL: Check if we just initialized the clock for THIS game
          // If so, skip Firebase sync to prevent stale data from corrupting fresh clock values
          const timeSinceInit = Date.now() - clockInitTimestampRef.current;
          const isWithinCooldown = lastInitializedGameIdRef.current === gameId &&
                                   timeSinceInit < CLOCK_INIT_COOLDOWN_MS;

          if (isWithinCooldown) {
          } else if (isStaleData) {
          } else if (data.lastMoveTimestamp) {
            // Skip self-sync: if we already handled this turn locally via handleTurnChange,
            // don't let our own Firebase echo overwrite the locally-applied increment.
            // The opponent hasn't called handleTurnChange yet so their lastHandledTurnRef
            // will be lower - they'll correctly proceed with the sync.
            const isLocalPlayer = user && gameState.lightPlayer === user.uid;
            const weJustMoved = isLocalPlayer ? !data.isLightTurn : data.isLightTurn;
            if (weJustMoved && data.turnCount <= lastHandledTurnRef.current) {
            } else {
            const beforeSyncState = TimeControlManager.getState();

            TimeControlManager.syncWithServerTime(
              {
                lightTimeRemaining: data.lightTimeRemaining,
                darkTimeRemaining: data.darkTimeRemaining,
                activePlayer: data.isLightTurn ? 'light' : 'dark',
                lastMoveTimestamp: data.lastMoveTimestamp
              },
              data.lastMoveTimestamp
            );

            // Log state AFTER sync
            const afterSyncState = TimeControlManager.getState();
            }
          } else {
          }
        }
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [gameId, gamePhase, gameState.isLightTurn]);

  // Handle turn changes for time control
  useEffect(() => {
    if (gamePhase !== 'playing') return;

    // CRITICAL: Don't trigger time control during active bonus turns (monkey swing, virus infection, etc.)
    // The turn hasn't actually changed yet - the same player still has control
    if (gameState.skipTurnChange || gameState.activeBonusTurnPieceId) {
      return;
    }

    // When the turn changes, update the timer
    const currentPlayer = gameState.isLightTurn ? 'light' : 'dark';
    const previousPlayer = gameState.isLightTurn ? 'dark' : 'light';

    // Only handle turn change after the first move (turnCount > 0)
    if (gameState.turnCount > 0) {
      TimeControlManager.handleTurnChange(previousPlayer, currentPlayer);
      lastHandledTurnRef.current = gameState.turnCount;
    }
  }, [gameState.isLightTurn, gameState.turnCount, gamePhase, gameState.skipTurnChange, gameState.activeBonusTurnPieceId]);

  // Time expiration handler - triggers game end and ELO processing on timeout
  useEffect(() => {
    if (gamePhase !== 'playing') return;

    const unsubscribe = TimeControlManager.subscribe((event) => {
      if (event.timeExpired && event.winner) {

        // Trigger game over through the engine's checkGameEnd
        // This will set gameState.state to PostGame and gameState.lightWon appropriately
        ZodiacEngineService.zodiacEngine.checkGameEnd();

        // Update local game state to trigger the MMR processing useEffect
        const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
        updateGameState(updatedGameState);

      }
    });

    return () => {
      unsubscribe();
    };
  }, [gamePhase, updateGameState]);

  // Handler for when rematch is accepted (defined before useEffect)
  const handleRematchAcceptedInline = async () => {
    if (!gameId || !user) return;


    try {
      // Store current player assignments BEFORE any state changes
      const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const previousLightPlayerId = currentGameState.lightPlayer;
      const previousDarkPlayerId = currentGameState.darkPlayer;
      const previousLightUsername = currentGameState.lightPlayerUsername;
      const previousDarkUsername = currentGameState.darkPlayerUsername;

      // SWAP colors for rematch - previous light becomes dark, previous dark becomes light
      const lightPlayerId = previousDarkPlayerId;
      const darkPlayerId = previousLightPlayerId;
      const lightUsername = previousDarkUsername;
      const darkUsername = previousLightUsername;
      const isLightPlayer = lightPlayerId === user.uid;


      // Reset time control — Firebase sync only for human games
      TimeControlManager.stopOnGameEnd(); // Clean up old local timers
      const timePreset = selectedTimeControl;
      const effectivePreset = timePreset === TimeControlPreset.ANY ? TimeControlPreset.STANDARD : timePreset;
      const timeConfig = getPresetConfig(effectivePreset);
      const isBotRematch = currentGameState.isBotGame;
      if (!isBotRematch) {
        await resetTimeControlInFirebase(gameId, timeConfig.initialTime);
      }

      // For bot rematches: unsubscribe from Firebase listener BEFORE resetting state
      // The listener would otherwise overwrite our local KingSelection reset with the
      // stale PostGame state still in Firebase (sendUpdateToRemote no-ops for Mode.AI)
      if (isBotRematch) {
        const eventManager = (ZodiacEngineService.zodiacEngine as any).eventManager;
        if (eventManager?.unsubscribeDocument) {
          eventManager.unsubscribeDocument();
          eventManager.unsubscribeDocument = undefined;
        }
      }

      // Clear the board (don't use resetGame() as it sets Dev mode with all pieces)
      (ZodiacEngineService.zodiacEngine as any).gameStateManager.emptyBoard();

      // Reset game state for a fresh game (bot = AI mode, human = Live mode)
      const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
      gameStateManager.setMode(isBotRematch ? Mode.AI : Mode.Live);
      gameStateManager.setGameStateState(State.KingSelection);
      gameStateManager.resetTurnCount();

      // Clear FEN history, king selections, and reset turn state
      const freshGameState = gameStateManager.getGameState();
      freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
      (freshGameState as any).repeatString = "";
      freshGameState.lightKing = null;
      freshGameState.darkKing = null;
      freshGameState.lightWon = undefined;
      freshGameState.isLightTurn = true;


      // Restore player assignments
      if (lightPlayerId) {
        gameStateManager.setLightPlayer(lightPlayerId);
      }
      if (darkPlayerId) {
        gameStateManager.setDarkPlayer(darkPlayerId);
      }
      if (lightUsername) {
        gameStateManager.setLightPlayerUsername(lightUsername);
      }
      if (darkUsername) {
        gameStateManager.setDarkPlayerUsername(darkUsername);
      }


      if (!isBotRematch) {
        // Sync NEW game state to Firebase FIRST
        // This ensures the Firebase listener receives the correct new state (KingSelection)
        // BEFORE we clear rematch fields (which would trigger listener with old state)
        await (ZodiacEngineService.zodiacEngine as any).eventManager.sendUpdateToRemote();

        // Small delay to ensure Firebase write propagates and listener fires with new state
        await new Promise(resolve => setTimeout(resolve, 200));

        // Clear rematch request fields (this will trigger listener but with KingSelection state)
        await clearRematchRequest(gameId);

        // Clear chat messages in Firebase
        await updateChatMessages(gameId, []);
      }

      // Clear local chat messages
      setChatMessages([]);

      // Close modals and clear rematch state
      setShowPostGameModal(false);
      setShowRematchRequest(false);
      setRematchRequestFrom(null);
      setGameResult(null);

      // Reset king reveal state for new game
      setIHaveRequestedReveal(false);
      setOpponentRequestedReveal(false);
      setMyRequestResponse(null);
      setOpponentRequestResponse(null);

      // Update local game state to trigger re-render
      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(newGameState);

      // Reset React component state
      setGamePhase('king-selection');
      setIsGameStarted(false);
      setSelectedLightKing(null);
      setSelectedDarkKing(null);
      // Light player selects first, so if we're light, we select immediately
      // If we're dark, we wait for light player to select first (unless it's a bot game)
      setIsSelectingForLight(isLightPlayer);
      setPlacedPieces(new Set());
      setIsLightPieceTurn(true);
      setWarningMessage(null);
      // If we're the dark player in a human game, wait for light to select king
      // In bot games, never wait — the bot handles its own selection via useEffect
      setIsWaitingForOpponent(!isLightPlayer && !isBotRematch);
      setWaitingMessage(!isLightPlayer && !isBotRematch ? 'Opponent is selecting their king' : '');

      // Re-initialize bot controller with swapped colors
      if (isBotRematch && botControllerRef.current) {
        botControllerRef.current.destroy();
        botControllerRef.current = null;
        const botId = currentGameState.botPlayerId;
        if (botId) {
          const botConfig = getBotConfig(botId);
          if (botConfig) {
            const newController = new BotGameController(
              ZodiacEngineService.zodiacEngine,
              botId,
              botConfig
            );
            const newState = ZodiacEngineService.zodiacEngine.getGameState();
            newController.initialize(newState);
            botControllerRef.current = newController;
          }
        }
      }

    } catch (error) {
      alert('Failed to reset game for rematch. Please refresh the page.');
    }
  };

  // Listen for rematch requests from opponent (skip for bot games — rematch is local)
  useEffect(() => {
    if (!gameId || !user) return;
    if (gameState.isBotGame) return;


    const unsubscribe = listenForDocumentChanges(gameId, (data) => {
      if (!data) return;

      // Check if there's a rematch request
      if (data.rematchRequestFrom && data.rematchRequestFrom !== user.uid) {
        setRematchRequestFrom(data.rematchRequestFrom);
        setShowRematchRequest(true);
      }

      // Check if opponent accepted our rematch request
      if (data.rematchAccepted && data.rematchRequestFrom === user.uid) {
        // Handle rematch acceptance inline
        handleRematchAcceptedInline();
      }

      // Check if opponent declined our rematch request
      if (data.rematchDeclined && data.rematchRequestFrom === user.uid) {
        alert('Your opponent declined the rematch request.');
        clearRematchRequest(gameId);
      }

      // Check for king reveal request/response updates
      const isLightPlayer = gameState.lightPlayer === user.uid;
      const myPrefix = isLightPlayer ? 'light' : 'dark';
      const opponentPrefix = isLightPlayer ? 'dark' : 'light';

      // Check if opponent sent us a request
      if (data[`kingRevealRequestBy_${opponentPrefix}`]) {
        setOpponentRequestedReveal(true);
      }

      // Check if opponent responded to our request
      const responseToMe = data[`kingRevealResponseTo_${myPrefix}`];
      if (responseToMe) {
        setMyRequestResponse(responseToMe);
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [gameId, user, gameState.lightPlayer]);

  // Fetch opponent profile and set username from gameState
  useEffect(() => {

    if (!user || !gameState.lightPlayer || !gameState.darkPlayer) {
      setOpponentProfile(null);
      setOpponentUsername('Unknown');
      return;
    }

    const isLightPlayer = gameState.lightPlayer === user.uid;
    const opponentId = isLightPlayer ? gameState.darkPlayer : gameState.lightPlayer;

    // Use username from gameState if available (fastest!)
    const opponentUsernameFromState = isLightPlayer
      ? gameState.darkPlayerUsername
      : gameState.lightPlayerUsername;

    if (opponentUsernameFromState) {
      setOpponentUsername(opponentUsernameFromState);
    }


    if (opponentId) {
      // Fetch MMR profile for ELO display using UserDataService (correct schema)
      UserDataService.getUserData(opponentId).then(userData => {
        if (userData && userData.profile) {
          const profile = userData.profile;
          // Log ALL MMR fields to help debug ELO discrepancy
          setOpponentProfile(profile);
          // Only update username if we don't have it from game state
          if (!opponentUsernameFromState) {
            setOpponentUsername(profile.username || 'Unknown');
          }
        } else {
        }
      }).catch(error => {
        setOpponentProfile(null);
      });
    }
  }, [user, gameState.lightPlayer, gameState.darkPlayer, gameState.lightPlayerUsername, gameState.darkPlayerUsername]);

  // Set player usernames in game state when both players are assigned
  useEffect(() => {
    if (!user || !userProfile) return;
    if (!gameState.lightPlayer || !gameState.darkPlayer) return;

    const isLightPlayer = gameState.lightPlayer === user.uid;

    // Set our username in the game state if not already set
    if (isLightPlayer && !gameState.lightPlayerUsername) {
      (ZodiacEngineService.zodiacEngine as any).gameStateManager.setLightPlayerUsername(userProfile.username || 'Unknown');
      updateGameState(ZodiacEngineService.zodiacEngine.getGameState());
    } else if (!isLightPlayer && !gameState.darkPlayerUsername) {
      (ZodiacEngineService.zodiacEngine as any).gameStateManager.setDarkPlayerUsername(userProfile.username || 'Unknown');
      updateGameState(ZodiacEngineService.zodiacEngine.getGameState());
    }
  }, [user, userProfile, gameState.lightPlayer, gameState.darkPlayer, gameState.lightPlayerUsername, gameState.darkPlayerUsername, updateGameState]);

  // Extract opponent username from chat messages as fallback
  useEffect(() => {
    if (!user || !chatMessages.length) return;

    // Find the first message not from us to get opponent's username
    const opponentMessage = chatMessages.find(msg => msg.username !== userProfile?.username);
    if (opponentMessage && (!opponentProfile || opponentUsername === 'Unknown')) {
      setOpponentUsername(opponentMessage.username);
    }
  }, [chatMessages, user, userProfile, opponentProfile, opponentUsername]);

  const handleHome = () => {
    navigate('/');
  };

  // Cancel/leave game during setup phases (king-selection, piece-placement)
  // Marks game as cancelled in Firebase (no winner) and returns to matchmaking
  const handleCancelGame = async () => {
    const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
    const eventManager = (ZodiacEngineService.zodiacEngine as any).eventManager;
    const currentGameId = ZodiacEngineService.zodiacEngine.getGameId()
      || eventManager?.getGameId?.();

    // Mark game as cancelled in Firebase
    if (currentGameId) {
      try {
        const { updateGamesDocument } = await import('../../services/firebase/firebase');
        await updateGamesDocument(currentGameId, {
          state: 'PostGame',
          winCondition: 'cancelled'
        });
        addDismissedGameId(currentGameId);
      } catch (error) {
      }
    }

    // Unsubscribe from Firebase listener
    if (eventManager?.unsubscribeDocument) {
      eventManager.unsubscribeDocument();
      eventManager.unsubscribeDocument = undefined;
    }
    if (eventManager) {
      eventManager.gameId = undefined;
    }

    // Reset local state
    setGameId(null);
    setContextGameId(null);
    setGamePhase('matchmaking');
    setIsWaitingForOpponent(false);
    setWaitingMessage('');
    setMatchFound(false);
    setSelectedLightKing(null);
    setSelectedDarkKing(null);
    setOpponentDisconnected(false);
    setShowDisconnectNotice(false);
    setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);
    justHandledForfeitRef.current = true;

    // Reset engine
    const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
    gameStateManager.emptyBoard();
    gameStateManager.setMode(Mode.Live);
    gameStateManager.setGameStateState(State.KingSelection);
    const freshGameState = gameStateManager.getGameState();
    freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
    freshGameState.lightKing = null;
    freshGameState.darkKing = null;
    freshGameState.lightWon = undefined;
    freshGameState.isLightTurn = true;
    freshGameState.lightPlayer = undefined;
    freshGameState.darkPlayer = undefined;
    updateGameState(freshGameState);
  };

  const getPieceImage = (zodiacResult: string) => {
    if (!zodiacResult) return '';
    const [variant, ...nameParts] = zodiacResult.split(' ');
    const pieceName = nameParts.join(' ');
    return `/Icons/${pieceName}-${variant.toLowerCase()}.png`;
  };

  const handleStartSearch = async () => {
    if (!user || !userProfile) {
      alert('Please wait for your profile to load');
      return;
    }


    // Ensure MMR profile is initialized - check if mmr exists AND has required fields
    const needsInitialization = !userProfile.mmr ||
                                  userProfile.mmr.provisionalRating === undefined ||
                                  userProfile.mmr.placementGames === undefined ||
                                  userProfile.mmr.colorBalance === undefined;

    if (needsInitialization) {
      try {
        const initializedProfile = await MMRService.initializeUserMMR(user);
        await updateProfile(initializedProfile);
      } catch (error) {
        alert('Failed to initialize your profile. Please try again.');
        return;
      }
    } else {
    }

    // Check if user is in primary (Marathon) placement
    const isInPrimaryPlacement = userProfile && !userProfile.mmr.timeControlRatings?.marathon?.isPlaced;

    // Placement players use 'placement' mode (converted to 'competitive' in queue, but tracked separately)
    const effectiveMode = isInPrimaryPlacement ? 'placement' : selectedMode;


    // Save the matchmaking mode for potential "Search Again" later
    setSavedMatchmakingMode(effectiveMode);

    try {
      // Determine time control preference
      let timeControlPref: 'sprint' | 'standard' | 'marathon' | 'any' = 'standard';

      // IMPORTANT: Force Marathon for PRIMARY placement games to give new players more time
      // This also prevents smurfs from speed-running through placement on new accounts
      // Also force Marathon for CASUAL mode - simplifies matchmaking, faster queue times, less time pressure
      if (isInPrimaryPlacement) {
        timeControlPref = 'marathon';
      } else if (effectiveMode === 'casual') {
        timeControlPref = 'marathon';
      } else {
        // Convert TimeControlPreset to matchmaking preference
        if (selectedTimeControl === TimeControlPreset.SPRINT) timeControlPref = 'sprint';
        else if (selectedTimeControl === TimeControlPreset.STANDARD) timeControlPref = 'standard';
        else if (selectedTimeControl === TimeControlPreset.MARATHON) timeControlPref = 'marathon';
        else if (selectedTimeControl === TimeControlPreset.ANY) timeControlPref = 'any';
      }

      // Determine time control for rating lookup
      let timeControlForRating: TimeControlType;
      if (isInPrimaryPlacement) {
        timeControlForRating = 'marathon';
      } else if (timeControlPref === 'any') {
        // For "Any" time control, use the highest placed rating
        timeControlForRating = 'marathon'; // Default, will be overridden by matchmaking
      } else {
        timeControlForRating = timeControlPref as TimeControlType;
      }

      // Get rating for the selected time control
      const userRating = MMRService.getCurrentRatingForTimeControl(userProfile, timeControlForRating);

      await matchmakingService.startSearch(
        {
          mode: effectiveMode,
          userId: user.uid,
          userRating: userRating,  // Use time-control-specific rating
          engine: ZodiacEngineService.zodiacEngine,
          timeControlPreference: timeControlPref
        },
        (status) => {
          setSearchStatus(status);

          // When match is found and WE JOINED an opponent's game
          if (status.matchFound && status.gameId && status.weJoined) {
            // Clear any king guesses from previous games
            clearAllKingGuesses();
            setGameId(status.gameId);
            setContextGameId(status.gameId);
            setMatchFound(true);

            // Don't manipulate state here - joinGame() in the engine already handled it
            // The Firebase listener will update our UI automatically
            // Just wait for the gameState sync to trigger phase change
          }
          // Bot match: we stay in our own game, bot was added directly
          if (status.matchFound && status.gameId && !status.weJoined) {
            clearAllKingGuesses();
            setGameId(status.gameId);
            setContextGameId(status.gameId);
            setMatchFound(true);
          }
        }
      );
    } catch (error) {
      alert('Failed to start matchmaking. Please try again.');
    }
  };

  const handleCancelSearch = async () => {
    await matchmakingService.cancelSearch();
    setSearchStatus({
      searching: false,
      playersInQueue: 0
    });
  };

  // Private game handlers
  const handleCreatePrivateGame = async () => {
    if (!user) return;
    setIsCreatingGame(true);
    try {
      const newGameId = await ZodiacEngineService.zodiacEngine.switchToLiveMove(user.uid);
      // Clear any king guesses from previous games
      clearAllKingGuesses();
      setPrivateGameId(newGameId);
      setGameId(newGameId);
      setContextGameId(newGameId);

      // Put the game ID in the input box and copy to clipboard
      setJoinGameId(newGameId);
      try {
        await navigator.clipboard.writeText(newGameId);
        setCopied(true);
        // Keep the "copied" message visible longer for visibility
        setTimeout(() => setCopied(false), 4000);
      } catch (clipboardErr) {
      }

      // Set as casual mode (no ELO impact)
      ZodiacEngineService.zodiacEngine.setMatchmakingMode('casual');

      // Store the selected time control in game state (will be initialized when game starts)
      // For private games, if "Any" is selected, use "Standard" as default
      const engineGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const effectiveTimeControl = selectedTimeControl === TimeControlPreset.ANY
        ? TimeControlPreset.STANDARD
        : selectedTimeControl;
      engineGameState.timeControlPreset = effectiveTimeControl;

      // Update local game state but stay in matchmaking phase
      // The Firebase listener will transition to king-selection when opponent joins
      updateGameState(engineGameState);
      // Don't change gamePhase yet - keep showing matchmaking UI with waiting state
    } catch (err) {
    } finally {
      setIsCreatingGame(false);
    }
  };

  const handleCancelPrivateGame = () => {
    // Reset private game state
    setPrivateGameId(null);
    setGameId(null);
    setContextGameId(null);
    setJoinGameId('');
    setCopied(false);
    setGamePhase('matchmaking');
    setIsWaitingForOpponent(false);
    setWaitingMessage('');
    // Clear board without starting a new game (same as mount effect)
    const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
    gameStateManager.emptyBoard();
    gameStateManager.setMode(Mode.Live);
    gameStateManager.setGameStateState(State.KingSelection);
    const freshGameState = gameStateManager.getGameState();
    freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
    updateGameState(freshGameState);
  };

  const copyPrivateGameId = async () => {
    if (!privateGameId) return;
    try {
      await navigator.clipboard.writeText(privateGameId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
    }
  };

  const handleJoinPrivateGame = async () => {
    if (!joinGameId.trim() || !user) return;
    setIsJoiningGame(true);
    try {
      await ZodiacEngineService.zodiacEngine.joinGame(joinGameId.trim(), user.uid, true);
      // Clear any king guesses from previous games
      clearAllKingGuesses();
      setGameId(joinGameId.trim());
      setContextGameId(joinGameId.trim());
      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(newGameState);
      setGamePhase('king-selection');
    } catch (err) {
      alert('Failed to join game. Please check the Game ID and try again.');
    } finally {
      setIsJoiningGame(false);
    }
  };

  // Spectate handlers
  const handleOpenSpectate = () => {
    setShowSpectateModal(true);
  };

  const handleStartSpectating = async (targetGameId: string) => {
    setShowSpectateModal(false);
    setIsSpectating(true);
    setContextIsSpectating(true); // Update context so SquareRenderer knows we're spectating
    setSpectatingGameId(targetGameId);
    setGameId(targetGameId);
    setContextGameId(targetGameId);

    // CRITICAL: Disable remote sync for spectators - they should never write to Firebase
    ZodiacEngineService.zodiacEngine.getEventManager().disableRemoteSync = true;

    // Fetch initial game state
    try {
      const gameData = await getGameStateByGameId(targetGameId);
      if (gameData) {
        const newGameState = convertFromFirebaseState(gameData);
        updateGameState(newGameState);
        setGamePhase('playing');
      }
    } catch (err) {
      setIsSpectating(false);
      setContextIsSpectating(false);
      setSpectatingGameId(null);
      setGameId(null);
      setContextGameId(null);
      // Re-enable remote sync on error
      ZodiacEngineService.zodiacEngine.getEventManager().disableRemoteSync = false;
    }
  };

  const handleStopSpectating = () => {
    setIsSpectating(false);
    setContextIsSpectating(false); // Update context
    setSpectatingGameId(null);
    setGameId(null);
    setContextGameId(null);
    setGamePhase('matchmaking');

    // Re-enable remote sync after spectating ends
    ZodiacEngineService.zodiacEngine.getEventManager().disableRemoteSync = false;

    // Reset game state
    ZodiacEngineService.zodiacEngine.resetAndStartNewGame();
    updateGameState(ZodiacEngineService.zodiacEngine.getGameState());
  };

  // Reconnect handlers - for players returning to an active game
  const handleReconnect = async () => {
    if (!activeGameToReconnect || !user) return;

    setIsReconnecting(true);
    setShowReconnectModal(false);

    try {

      // Use the engine's joinGame which handles reconnection
      await ZodiacEngineService.zodiacEngine.joinGame(activeGameToReconnect.id, user.uid, false);

      // Clear king guesses from previous games
      clearAllKingGuesses();

      // Set up local state
      setGameId(activeGameToReconnect.id);
      setContextGameId(activeGameToReconnect.id);

      // Get the updated game state
      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(newGameState);

      // Determine the game phase based on game state
      // State enum values: "Piece Selection", "King Selection", "Playing" (with spaces!)
      const gameData = activeGameToReconnect.data;
      if (gameData.state === 'Piece Selection' || gameData.state === 'King Selection') {
        setGamePhase('piece-placement');
      } else {
        setGamePhase('playing');
      }

      // Update presence immediately after reconnect - directly to Firebase
      const eventManager = ZodiacEngineService.zodiacEngine.getEventManager();
      const reconnectGameId = eventManager.getGameId();
      const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
      const isLightPlayer = currentGameState.lightPlayer === user.uid;
      const timestamp = Date.now();

      if (reconnectGameId) {
        const { updateGamesDocument } = await import('../../services/firebase/firebase');
        const presenceUpdate = isLightPlayer
          ? { lightPlayerTabHidden: false, lightPlayerLastSeen: timestamp }
          : { darkPlayerTabHidden: false, darkPlayerLastSeen: timestamp };
        await updateGamesDocument(reconnectGameId, presenceUpdate);
      } else {
      }

      // CRITICAL: Clear opponent disconnected state after successful reconnection
      // The reconnecting player's opponent was never disconnected - they stayed in the game
      // Also reset the monitoring start time for fresh presence checking
      setOpponentDisconnected(false);
      setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);
      monitoringStartTimeRef.current = Date.now();

    } catch (error) {
      alert('Failed to reconnect to game. The game may have ended.');
      setShowReconnectModal(false);
    } finally {
      setIsReconnecting(false);
      setActiveGameToReconnect(null);
    }
  };

  const handleDismissReconnect = async () => {
    // IMMEDIATELY stop all further reconnect checks for this session
    // This prevents infinite modal loop when there are many old games
    userForfeitedAllGamesRef.current = true;

    if (!user) {
      setShowReconnectModal(false);
      setActiveGameToReconnect(null);
      return;
    }

    // Mark ALL active games as forfeit to prevent modal loop
    try {
      const { updateGamesDocument, queryActiveGamesForUser } = await import('../../services/firebase/firebase');

      // Get all active games for this user
      const allActiveGames = await queryActiveGamesForUser(user.uid);

      // Mark each one as forfeit and add to dismissed set (persisted to localStorage)
      for (const game of allActiveGames) {
        const isLightPlayer = game.data.lightPlayer === user.uid;
        addDismissedGameId(game.id);

        await updateGamesDocument(game.id, {
          lightWon: !isLightPlayer, // Opponent wins
          state: 'PostGame',
          winCondition: 'forfeit'
        });
      }

      // Also add the currently shown game if it exists
      if (activeGameToReconnect) {
        addDismissedGameId(activeGameToReconnect.id);
      }

    } catch (error) {
    }

    setShowReconnectModal(false);
    setActiveGameToReconnect(null);
  };

  // King selection handler
  const handleKingSelected = (king: PieceType, isLight: boolean) => {

    // Call engine's setKing which will sync to Firebase
    ZodiacEngineService.zodiacEngine.setKing(king, isLight);

    // Update local state
    if (isLight) {
      setSelectedLightKing(king);
    } else {
      setSelectedDarkKing(king);
    }

    // Update local game state to trigger the sync useEffect
    const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(updatedGameState);

    // The useEffect will handle transitioning to piece placement once both kings are selected
  };

  // Piece placement handlers
  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    if (gamePhase !== 'piece-placement') return;
    if (!user) return;

    event.preventDefault();
    const pieceData = event.dataTransfer.getData("piece");
    if (!pieceData) return;

    const parsedData = JSON.parse(pieceData);
    const { type, isLight } = parsedData;

    // Check if it's this player's turn (matches BoardContainer logic)
    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isDarkPlayer = gameState.darkPlayer === user.uid;
    const isThisPlayersTurn = (isLightPlayer && gameState.isLightTurn) || (isDarkPlayer && !gameState.isLightTurn);

    if (!isThisPlayersTurn) {
      setWarningMessage(`It's ${gameState.isLightTurn ? 'Light' : 'Dark'} player's turn!`);
      return;
    }

    // In multiplayer, players can only place their own color pieces
    if (isLightPlayer && !isLight) {
      setWarningMessage('You can only place light pieces!');
      return;
    }
    if (isDarkPlayer && isLight) {
      setWarningMessage('You can only place dark pieces!');
      return;
    }

    const requiredRow = isLight ? 0 : 7;
    if (position.row !== requiredRow) {
      setWarningMessage(`${isLight ? 'Light' : 'Dark'} pieces must be placed on row ${requiredRow}!`);
      return;
    }

    // Check if piece already placed
    const pieceKey = `${type}-${isLight ? 'light' : 'dark'}`;
    if (placedPieces.has(pieceKey)) {
      return;
    }

    try {
      // Use setStartingPiece which syncs to Firebase and changes turns
      ZodiacEngineService.zodiacEngine.setStartingPiece(type as PieceType, isLight, position.col);

      setPlacedPieces(prev => new Set(prev).add(pieceKey));

      // AUTO-SELECT KING: If this is the last piece being placed and no king was selected, auto-assign it as king
      const newPlacedPieces = new Set(placedPieces).add(pieceKey);
      const playerPieces = Array.from(newPlacedPieces).filter(key =>
        key.endsWith(isLight ? '-light' : '-dark')
      );

      if (playerPieces.length === 5) {
        const selectedKing = isLight ? selectedLightKing : selectedDarkKing;
        if (!selectedKing) {
          // Auto-select this piece as the king
          ZodiacEngineService.zodiacEngine.setKing(type as PieceType, isLight);
          if (isLight) {
            setSelectedLightKing(type as PieceType);
          } else {
            setSelectedDarkKing(type as PieceType);
          }
        }
      }

      // Play sound
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      // Update local game state to trigger sync
      const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(updatedGameState);

    } catch (error) {
      if (error instanceof Error) {
        setWarningMessage(error.message);
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handlePieceDragStart = (event: React.DragEvent<HTMLImageElement>, pieceType: PieceType, isLight: boolean) => {
    event.dataTransfer.setData(
      "piece",
      JSON.stringify({ type: pieceType, isLight })
    );
  };

  // Mobile square click handler for piece placement
  const handleMobileSquareClick = (position: { row: number; col: number }) => {
    if (!isMobile || gamePhase !== 'piece-placement' || !selectedPieceForPlacement || !user) {
      return;
    }

    const { type, isLight } = selectedPieceForPlacement;

    // Check if it's this player's turn
    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isDarkPlayer = gameState.darkPlayer === user.uid;
    const isThisPlayersTurn = (isLightPlayer && gameState.isLightTurn) || (isDarkPlayer && !gameState.isLightTurn);

    if (!isThisPlayersTurn) {
      setWarningMessage(`It's ${gameState.isLightTurn ? 'Light' : 'Dark'} player's turn!`);
      return;
    }

    // In multiplayer, players can only place their own color pieces
    if (isLightPlayer && !isLight) {
      setWarningMessage('You can only place light pieces!');
      return;
    }
    if (isDarkPlayer && isLight) {
      setWarningMessage('You can only place dark pieces!');
      return;
    }

    const requiredRow = isLight ? 0 : 7;
    if (position.row !== requiredRow) {
      setWarningMessage(`${isLight ? 'Light' : 'Dark'} pieces must be placed on row ${requiredRow}!`);
      return;
    }

    // Check if piece already placed
    const pieceKey = `${type}-${isLight ? 'light' : 'dark'}`;
    if (placedPieces.has(pieceKey)) {
      return;
    }

    try {
      ZodiacEngineService.zodiacEngine.setStartingPiece(type as PieceType, isLight, position.col);
      setPlacedPieces(prev => new Set(prev).add(pieceKey));
      setSelectedPieceForPlacement(null);

      // AUTO-SELECT KING: If this is the last piece being placed and no king was selected, auto-assign it as king
      const newPlacedPieces = new Set(placedPieces).add(pieceKey);
      const playerPieces = Array.from(newPlacedPieces).filter(key =>
        key.endsWith(isLight ? '-light' : '-dark')
      );

      if (playerPieces.length === 5) {
        const selectedKing = isLight ? selectedLightKing : selectedDarkKing;
        if (!selectedKing) {
          // Auto-select this piece as the king
          ZodiacEngineService.zodiacEngine.setKing(type as PieceType, isLight);
          if (isLight) {
            setSelectedLightKing(type as PieceType);
          } else {
            setSelectedDarkKing(type as PieceType);
          }
        }
      }

      // Play sound
      const audio = new Audio('/turn change.mp3');
      audio.volume = 0.3;
      audio.play().catch(console.error);

      const updatedGameState = ZodiacEngineService.zodiacEngine.getGameState();
      updateGameState(updatedGameState);
    } catch (error) {
      if (error instanceof Error) {
        setWarningMessage(error.message);
      }
    }
  };

  const handleMobileBack = () => {
    setIsMobileGamePage(false);
    // Don't reset game - just go back to matchmaking view
    // The game continues in the background
  };

  // Detect when opponent tries to chat (for temp accounts to show notification)
  useEffect(() => {
    // Only track for temp accounts
    if (accountType !== 'temp') return;

    // Check if opponent has sent any messages
    const hasOpponentMessage = chatMessages.some(msg => msg.username !== userProfile?.username);
    if (hasOpponentMessage && !opponentTriedToChat) {
      setOpponentTriedToChat(true);
    }
  }, [chatMessages, accountType, userProfile?.username, opponentTriedToChat]);

  // Chat handlers
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !userProfile || !gameId || isSendingChat) return;

    setIsSendingChat(true);

    // Add (spectator) suffix if spectating
    const displayName = isSpectating
      ? `${userProfile.username || 'Anonymous'} (spectator)`
      : userProfile.username || 'Anonymous';

    const newMessage = {
      username: displayName,
      text: chatInput.trim(),
      timestamp: Date.now()
    };

    const updatedMessages = [...chatMessages, newMessage];

    try {
      // Send to Firebase
      await updateChatMessages(gameId, updatedMessages);
      setChatMessages(updatedMessages);
      setChatInput('');
    } catch (error) {
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSendingChat(false);
    }
  };

  // Rematch handlers
  const handleRequestRematch = async () => {
    if (!gameId || !user) return;

    // AI always accepts rematch (with humanized delay)
    if (gameState.isBotGame) {
      setShowPostGameModal(false);
      try {
        await botDelay();
        await handleRematchAcceptedInline();
      } catch (error) {
      }
      return;
    }

    try {
      await sendRematchRequest(gameId, user.uid);
      setShowPostGameModal(false);
    } catch (error) {
      alert('Failed to send rematch request. Please try again.');
    }
  };

  const handleAcceptRematch = async () => {
    if (!gameId) return;

    try {
      await acceptRematchRequest(gameId);
      setShowRematchRequest(false);
      await handleRematchAcceptedInline();
    } catch (error) {
      alert('Failed to accept rematch. Please try again.');
    }
  };

  const handleDeclineRematch = async () => {
    if (!gameId) return;

    try {
      await declineRematchRequest(gameId);
      setShowRematchRequest(false);
      setRematchRequestFrom(null);
    } catch (error) {
    }
  };

  // King reveal request handler (post-game)
  const handleRequestKingReveal = async () => {
    if (!user || iHaveRequestedReveal) return;

    // AI always accepts reveal requests (with humanized delay)
    if (gameState.isBotGame) {
      setIHaveRequestedReveal(true);
      botDelay().then(() => setMyRequestResponse('accepted'));
      return;
    }

    if (!gameId) return;
    const isLightPlayer = gameState.lightPlayer === user.uid;
    try {
      await sendKingRevealRequest(gameId, isLightPlayer);
      setIHaveRequestedReveal(true);
    } catch (error) {
    }
  };

  // King reveal response handler (post-game)
  const handleRespondToKingReveal = async (accepted: boolean) => {
    if (!gameId || !user) return;
    const isLightPlayer = gameState.lightPlayer === user.uid;
    try {
      await respondToKingRevealRequest(gameId, isLightPlayer, accepted);
      setOpponentRequestResponse(accepted ? 'accepted' : 'declined');
    } catch (error) {
    }
  };

  // Close modal and return to multiplayer lobby (game type selection) without auto-searching
  const handleBackToLobby = () => {

    setShowPostGameModal(false);
    setGamePhase('matchmaking');
    setMatchFound(false);
    setGameId(null);
    setContextGameId(null);
    setGameResult(null);
    setIsGameStarted(false);
    setSelectedLightKing(null);
    setSelectedDarkKing(null);
    setPlacedPieces(new Set());
    setIHaveRequestedReveal(false);
    setOpponentRequestedReveal(false);
    setMyRequestResponse(null);
    setOpponentRequestResponse(null);
    clearAllKingGuesses();

    // Reset game state for a fresh lobby
    const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
    gameStateManager.emptyBoard();
    gameStateManager.setMode(Mode.Live);
    gameStateManager.setGameStateState(State.KingSelection);
    const freshGameState = gameStateManager.getGameState();
    freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
    freshGameState.lightKing = null;
    freshGameState.darkKing = null;
    freshGameState.lightWon = undefined;
    freshGameState.isLightTurn = true;
    freshGameState.lightPlayer = undefined;
    freshGameState.darkPlayer = undefined;
    updateGameState(freshGameState);
  };

  const handleSearchAgain = async () => {
    if (!user || !userProfile) return;


    // Close post-game modal
    setShowPostGameModal(false);

    // Reset to matchmaking phase
    setGamePhase('matchmaking');
    setMatchFound(false);
    setGameId(null);
    setContextGameId(null);
    setGameResult(null);

    // Clear game state
    setIsGameStarted(false);
    setSelectedLightKing(null);
    setSelectedDarkKing(null);
    setPlacedPieces(new Set());

    // Reset king reveal state for new search
    setIHaveRequestedReveal(false);
    setOpponentRequestedReveal(false);
    setMyRequestResponse(null);
    setOpponentRequestResponse(null);

    // Clear any king guesses from previous games
    clearAllKingGuesses();

    // Start a new search with the same settings
    try {
      await matchmakingService.startSearch(
        {
          mode: savedMatchmakingMode,
          userId: user.uid,
          userRating: MMRService.getHighestRating(userProfile).rating,
          engine: ZodiacEngineService.zodiacEngine
        },
        (status) => {
          setSearchStatus(status);

          // When match is found and WE JOINED an opponent's game
          if (status.matchFound && status.gameId && status.weJoined) {
            clearAllKingGuesses();
            setGameId(status.gameId);
            setContextGameId(status.gameId);
            setMatchFound(true);
          }
        }
      );
    } catch (error) {
      alert('Failed to start matchmaking. Please try again.');
    }
  };

  const renderPlacementUI = () => {
    if (!userProfile) return null;

    // Use Marathon-specific placement data for primary placement
    const marathonRating = userProfile.mmr.timeControlRatings?.marathon;
    const placementGames = marathonRating?.placementGames || userProfile.mmr.placementGames || 0;
    const placementHistory = userProfile.mmr.placementHistory || [];
    const provisionalRating = Math.round(marathonRating?.provisionalRating || userProfile.mmr.provisionalRating);

    // Show as a range to indicate uncertainty (±100)
    const rangeSize = 100;
    const minElo = provisionalRating - rangeSize;
    const maxElo = provisionalRating + rangeSize;
    const estimatedEloRange = `${minElo}-${maxElo}`;

    // Create array of 10 slots for the grid
    const slots = Array.from({ length: 10 }, (_, index) => {
      if (index < placementGames) {
        const game = placementHistory[index];
        return {
          // Use 'unknown' for completed games without history (legacy data)
          status: game?.result || 'unknown',
          index
        };
      }
      return { status: 'pending', index };
    });

    return (
      <>
        <div className={styles.header}>
          <span className={styles.headerIcon}></span>
          <span className={styles.headerText}>{t('multiplayer.placementMatches')}</span>
        </div>

        <div className={styles.placementContainer}>
          <div className={styles.placementProgress}>
            <div className={styles.progressLabel}>{t('multiplayer.matchProgress')}</div>
            <div className={styles.placementGrid}>
              {slots.map((slot, i) => (
                <React.Fragment key={i}>
                  <span
                    className={`${styles.placementSlot} ${
                      slot.status === 'win'
                        ? styles.win
                        : slot.status === 'loss'
                        ? styles.loss
                        : slot.status === 'draw'
                        ? styles.draw
                        : slot.status === 'unknown'
                        ? styles.unknown
                        : styles.pending
                    }`}
                  >
                    {slot.status === 'win' && 'W'}
                    {slot.status === 'loss' && 'L'}
                    {slot.status === 'draw' && 'D'}
                    {slot.status === 'unknown' && '?'}
                    {slot.status === 'pending' && (slot.index + 1)}
                  </span>
                  {i < 9 && <span className={styles.placementSeparator}>·</span>}
                </React.Fragment>
              ))}
            </div>
            <div className={styles.gamesCompletedText}>
              {t('multiplayer.gamesCompleted', { games: placementGames.toString() })}
            </div>
          </div>

          <div className={styles.estimatedElo}>
            <div className={styles.estimatedLabel}>{t('multiplayer.estimatedFinalRating')}</div>
            <div className={styles.estimatedValue}>{estimatedEloRange}</div>
            <div className={styles.estimatedNote}>
              {placementGames === 0
                ? t('multiplayer.startingRating')
                : t('multiplayer.matchesRemaining', { matches: (10 - placementGames).toString() })}
            </div>
          </div>

          <button
            className={styles.placementButton}
            onClick={handleStartSearch}
            disabled={!user || !userProfile}
          >
            <span className={styles.placementButtonIcon}></span>
            <span>{t('multiplayer.findPlacementMatch')}</span>
          </button>
        </div>
      </>
    );
  };

  const getModeDescription = (mode: MatchmakingMode): string => {
    switch (mode) {
      case 'competitive':
        return t('multiplayer.competitiveDesc');
      case 'casual':
        return t('multiplayer.casualDesc');
      default:
        return '';
    }
  };

  const getModeIcon = (mode: MatchmakingMode): string => {
    switch (mode) {
      case 'competitive':
        return '';
      case 'casual':
        return '';
      default:
        return '';
    }
  };

  // Render piece selection UI
  const renderPieceSelection = () => {
    if (!user) return null;

    const isLightPlayer = gameState.lightPlayer === user.uid;
    const isLight = isLightPlayer;
    const selectedKing = isLight ? selectedLightKing : selectedDarkKing;

    return (
      <div className={sharedStyles.piecePaletteContainer}>
        <h3 className={sharedStyles.piecePaletteTitle}>Drag pieces to the board</h3>
        <PieceSelectionPanel
          placedPieces={placedPieces}
          selectedKing={selectedKing}
          isLight={isLight}
          onPieceDragStart={handlePieceDragStart}
        />
      </div>
    );
  };

  // Mobile game page - separate render for mobile when in game
  if (isMobile && isMobileGamePage) {
    const isLightPlayer = user ? gameState.lightPlayer === user.uid : false;
    const isDarkPlayer = user ? gameState.darkPlayer === user.uid : false;
    const playerIsLight = isLightPlayer;

    return (
      <AdSenseLayout showAds={false}>
        <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app} ${styles.mobileGamePage}`}>
          <WispyStars />
          <GlobalHeader />
          <TurnPopover
            isThisPlayersTurn={gameState.isLightTurn === playerIsLight}
            state={gameState.state}
            mode={gameState.mode}
          />

          <div className={styles.mobileGameContainer} style={{ position: 'relative' }}>
            {/* Dark Timer (above board) — hidden entirely when preset is OFF */}
            {gamePhase === 'playing' && isTimedPreset((gameState.timeControlPreset || 'standard') as TimeControlPreset) && (
              <div style={{ display: 'flex', width: '100%', paddingLeft: '10px' }}>
                <TimerDisplay player="dark" isActive={gameState.isLightTurn === false} />
              </div>
            )}

            <div className={styles.boardContainer}>
              <Board
                gameState={gameState}
                uiState={{
                  potentialMoves: potentialMoves,
                  potentialSideEffects: potentialSideEffects,
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onSquareClick={gamePhase === 'piece-placement' && selectedPieceForPlacement ? handleMobileSquareClick : undefined}
                highlightedRows={gamePhase === 'piece-placement' ? {
                  light: playerIsLight && gameState.isLightTurn ? 0 : null,
                  dark: !playerIsLight && !gameState.isLightTurn ? 7 : null
                } : undefined}
              />

              {/* King Selection Overlay for Mobile */}
              {gamePhase === 'king-selection' && (
                <div className={styles.mobileOverlay}>
                  <div className={styles.mobileOverlayContent}>
                    <h3>Select Your King</h3>
                    <div className={styles.mobileKingGrid}>
                      {allPieceTypes
                        .filter((pieceType) => pieceType !== PieceType.Virus)
                        .map((pieceType) => (
                        <div
                          key={pieceType}
                          className={styles.mobileKingOption}
                          onClick={() => handleKingSelected(pieceType, playerIsLight)}
                        >
                          <img
                            src={`/Icons/${pieceType}-${playerIsLight ? 'light' : 'dark'}.png`}
                            alt={pieceType}
                            className={styles.mobileKingIcon}
                          />
                          <span>{pieceType}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Piece Placement Selector for Mobile */}
              {gamePhase === 'piece-placement' && (() => {
                const sideSuffix = playerIsLight ? 'light' : 'dark';
                const placedCountForSide = Array.from(placedPieces).filter(k => k.endsWith(`-${sideSuffix}`)).length;
                const kingType = playerIsLight ? selectedLightKing : selectedDarkKing;
                const kingKey = kingType ? `${kingType}-${sideSuffix}` : null;
                const kingIsPlaced = kingKey ? placedPieces.has(kingKey) : false;
                const mustPlaceKing = kingType !== null && placedCountForSide === 4 && !kingIsPlaced;

                return (
                  <div className={styles.mobilePieceSelector}>
                    <h4>Select piece to place</h4>
                    <div className={styles.mobilePieceGrid}>
                      {allPieceTypes
                        .filter((pieceType) => {
                          const pieceKey = `${pieceType}-${sideSuffix}`;
                          return !placedPieces.has(pieceKey);
                        })
                        .map((pieceType) => {
                          const isKing = pieceType === kingType;
                          const isSelected = selectedPieceForPlacement?.type === pieceType;
                          const isDisabled = mustPlaceKing && !isKing;

                          return (
                            <div
                              key={pieceType}
                              className={`${styles.mobilePieceOption} ${isSelected ? styles.mobilePieceSelected : ''} ${isDisabled ? styles.mobilePieceDisabled : ''}`}
                              onClick={() => {
                                if (isDisabled) return;
                                setSelectedPieceForPlacement({ type: pieceType, isLight: playerIsLight });
                              }}
                            >
                              {isKing && <div className={styles.mobileKingBadge}></div>}
                              <img
                                src={`/Icons/${pieceType}-${playerIsLight ? 'light' : 'dark'}.png`}
                                alt={pieceType}
                                className={styles.mobilePieceIcon}
                              />
                              <span className={styles.mobilePieceName}>{pieceType}</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Light Timer (below board) — hidden entirely when preset is OFF */}
            {gamePhase === 'playing' && isTimedPreset((gameState.timeControlPreset || 'standard') as TimeControlPreset) && (
              <div style={{ display: 'flex', width: '100%', paddingLeft: '10px' }}>
                <TimerDisplay player="light" isActive={gameState.isLightTurn === true} />
              </div>
            )}

            <div className={styles.mobileButtons}>
              <button className={styles.mobileTurnIndicator}>
                {gameState.isLightTurn ? 'Light Turn' : 'Dark Turn'}
              </button>
              {gamePhase === 'playing' && !isSpectating && (
                <button
                  className={`${styles.mobileTurnIndicator} ${kingHighlightActive ? styles.showKingButtonActive : ''}`}
                  onClick={activateKingHighlight}
                  disabled={kingHighlightActive}
                >
                  {kingHighlightActive ? 'KING' : 'SHOW KING'}
                </button>
              )}
              <button className={styles.mobileBackButton} onClick={() => { playUISound(); handleMobileBack(); }}>
                Back
              </button>
            </div>
          </div>

          {/* Warning Modal */}
          {warningMessage && (
            <div className={styles.modalOverlay}>
              <div className={styles.warningModal} onClick={(e) => e.stopPropagation()}>
                <h2>Invalid Action</h2>
                <p>{warningMessage}</p>
                <button className={styles.modalButton} onClick={() => setWarningMessage(null)}>OK</button>
              </div>
            </div>
          )}

          {/* Post-game modal */}
          {showPostGameModal && gameResult && (
            <>
              <PostGameModal
                isOpen={showPostGameModal}
                gameResult={gameResult}
                onClose={() => setShowPostGameModal(false)}
                onRematch={handleRequestRematch}
                onSearchAgain={handleHome}
                onBackToLobby={handleBackToLobby}
                winCondition={winCondition}
                myKing={gameState.lightPlayer === user?.uid ? gameState.lightKing : gameState.darkKing}
                opponentKing={gameState.lightPlayer === user?.uid ? gameState.darkKing : gameState.lightKing}
                isLightPlayer={gameState.lightPlayer === user?.uid}
                iHaveRequestedReveal={iHaveRequestedReveal}
                opponentRequestedReveal={opponentRequestedReveal}
                myRequestResponse={myRequestResponse}
                opponentRequestResponse={opponentRequestResponse}
                onRequestKingReveal={handleRequestKingReveal}
                onRespondToKingReveal={handleRespondToKingReveal}
              />
            </>
          )}
        </div>
      </AdSenseLayout>
    );
  }

  return (
    <AdSenseLayout>
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
        <WispyStars />
        <GlobalHeader />

        {/* Home button - top left */}
        <button
          className={styles.homeBackButton}
          onClick={() => { playUISound(); handleHome(); }}
          aria-label={t('common.home')}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <TurnPopover
          isThisPlayersTurn={gameState.isLightTurn === true}
          state={gameState.state}
          mode={gameState.mode}
        />

      {/* Left Panel */}
      <div className={styles.leftPanel}>
        <h1 className={styles.title}>{t('multiplayer.title')}</h1>

        {/* Spectating Banner */}
        {isSpectating && (
          <div className={styles.spectatingBanner}>
            <span className={styles.spectatingText}>👁 Spectating</span>
            <button
              className={styles.stopSpectatingButton}
              onClick={handleStopSpectating}
            >
              Leave
            </button>
          </div>
        )}

        <div className={styles.description}>
          <p>{t('multiplayer.description')}</p>
        </div>

        {/* Profile button */}
        {userProfile && (
          <button
            className={styles.profileSection}
            onClick={() => setShowProfileModal(true)}
            title="View Profile"
          >
            <div className={styles.profileAvatar}>
              {userProfile.zodiacResult ? (
                <img
                  src={getPieceImage(userProfile.zodiacResult)}
                  alt="Your zodiac piece"
                  className={styles.zodiacAvatar}
                />
              ) : (
                <div className={styles.avatarLetter}>
                  {userProfile.username ? userProfile.username.charAt(0).toUpperCase() : '?'}
                </div>
              )}
            </div>
            <div className={styles.profileInfo}>
              <div className={styles.profileUsername}>{userProfile.username || 'Anonymous'}</div>
              <div className={styles.profileElo}>
                {(() => {
                  // Casual mode doesn't show rating - just username is cleaner
                  if (selectedMode === 'casual') {
                    return null;
                  }

                  // Determine which time control rating to display based on selection
                  // "Any" shows Standard rating
                  const displayTimeControl: TimeControlType =
                    selectedTimeControl === TimeControlPreset.ANY ? 'standard' :
                    selectedTimeControl === TimeControlPreset.SPRINT ? 'sprint' :
                    selectedTimeControl === TimeControlPreset.MARATHON ? 'marathon' :
                    'standard';

                  const tcRating = userProfile.mmr.timeControlRatings?.[displayTimeControl];

                  if (tcRating?.isPlaced) {
                    return (
                      <>
                        <span className={styles.trophyIcon}>🏆</span>
                        {tcRating.rating} ({displayTimeControl.toUpperCase()})
                      </>
                    );
                  } else {
                    // Not placed in this time control - show placement progress (0/10 if no games played)
                    const placementGames = tcRating?.placementGames ?? 0;
                    return `${displayTimeControl.charAt(0).toUpperCase() + displayTimeControl.slice(1)} Placement ${placementGames}/10`;
                  }
                })()}
              </div>
            </div>
          </button>
        )}

        {/* Matchmaking UI - Only show when in matchmaking phase */}
        {gamePhase === 'matchmaking' && (
          <div className={styles.matchmakingContainer}>
            {!searchStatus.searching ? (
              isInPlacement ? (
                renderPlacementUI()
              ) : (
                <>
                  {/* Tab Toggle */}
                  <div className={styles.matchmakingTabs}>
                    <button
                      className={`${styles.matchmakingTab} ${matchmakingTab === 'findMatch' ? styles.matchmakingTabActive : ''}`}
                      onClick={() => setMatchmakingTab('findMatch')}
                    >
                      {t('multiplayer.findMatch')}
                    </button>
                    <button
                      className={`${styles.matchmakingTab} ${matchmakingTab === 'privateGame' ? styles.matchmakingTabActive : ''}`}
                      onClick={() => setMatchmakingTab('privateGame')}
                    >
                      Private Game
                    </button>
                  </div>

                  {/* Find Match Content - Mode Toggle */}
                  {matchmakingTab === 'findMatch' && (
                    <>
                      <div className={styles.modeToggleContainer}>
                        <span
                          className={`${styles.modeToggleLabel} ${selectedMode === 'casual' ? styles.modeToggleLabelActive : ''}`}
                          onClick={() => setSelectedMode('casual')}
                        >
                          {t('multiplayer.casual')}
                        </span>
                        <label className={styles.modeToggleSwitch}>
                          <input
                            type="checkbox"
                            checked={selectedMode === 'competitive'}
                            onChange={(e) => setSelectedMode(e.target.checked ? 'competitive' : 'casual')}
                          />
                          <span className={styles.modeToggleSlider}></span>
                        </label>
                        <span
                          className={`${styles.modeToggleLabel} ${selectedMode === 'competitive' ? styles.modeToggleLabelActive : ''}`}
                          onClick={() => setSelectedMode('competitive')}
                        >
                          {t('multiplayer.competitive')}
                        </span>
                      </div>
                      <div className={styles.modeToggleDescription}>
                        {getModeDescription(selectedMode)}
                      </div>
                    </>
                  )}

                  {/* Private Game Content */}
                  {matchmakingTab === 'privateGame' && (
                    <>
                      {!privateGameId ? (
                        <>
                          {/* Create Game Button */}
                          <button
                            className={styles.createGameButton}
                            onClick={handleCreatePrivateGame}
                            disabled={isCreatingGame}
                          >
                            {isCreatingGame ? 'Creating...' : 'Create Game ID'}
                          </button>

                          {/* Divider */}
                          <div className={styles.privateDivider}>
                            <span>or</span>
                          </div>

                          {/* Join Game Section */}
                          <div className={styles.joinGameSection}>
                            <input
                              type="text"
                              className={styles.gameIdInput}
                              placeholder="Paste Game ID..."
                              value={joinGameId}
                              onChange={(e) => setJoinGameId(e.target.value)}
                            />
                            <button
                              className={styles.joinGameButton}
                              onClick={handleJoinPrivateGame}
                              disabled={!joinGameId.trim() || isJoiningGame}
                            >
                              {isJoiningGame ? 'Joining...' : 'Join'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Game Created - Waiting for opponent */}
                          <div className={styles.privateGameWaiting}>
                            <div className={styles.gameIdDisplayBox}>
                              <span className={styles.gameIdDisplayLabel}>Your Game ID</span>
                              <span className={styles.gameIdDisplayValue}>{privateGameId}</span>
                            </div>
                            <div className={styles.waitingForOpponent}>
                              <div className={styles.waitingSpinner}></div>
                              <span>Awaiting opponent</span>
                            </div>
                            <button
                              className={styles.cancelGameButton}
                              onClick={handleCancelPrivateGame}
                            >
                              Cancel Game
                            </button>
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* Time Control Selector - shared between Find Match and Private Game */}
                  {/* Hide when private game is created OR when casual mode is selected (casual always uses Marathon) */}
                  {!privateGameId && selectedMode === 'competitive' && (
                    <div className={styles.timeControlSection}>
                      <div className={styles.timeControlHeader}>
                        <span className={styles.timeControlIcon}></span>
                        <span className={styles.timeControlLabel}>Time Control</span>
                      </div>
                      <div className={styles.timeControlButtons}>
                        {/* Time control buttons with placement badges for competitive mode */}
                        {[TimeControlPreset.SPRINT, TimeControlPreset.STANDARD, TimeControlPreset.MARATHON].map((preset) => {
                          // Map preset to TimeControlType
                          const tcType: TimeControlType = preset === TimeControlPreset.SPRINT ? 'sprint'
                            : preset === TimeControlPreset.MARATHON ? 'marathon'
                            : 'standard';
                          const status = getPlacementStatus(tcType);

                          return (
                            <button
                              key={preset}
                              className={`${styles.timeControlButton} ${
                                selectedTimeControl === preset ? styles.timeControlButtonActive : ''
                              }`}
                              onClick={() => {
                                setSelectedTimeControl(preset);
                                localStorage.setItem('zodiac-time-control', preset);
                              }}
                            >
                              <div className={styles.timeControlName}>
                                {preset === TimeControlPreset.SPRINT && 'Sprint'}
                                {preset === TimeControlPreset.STANDARD && 'Standard'}
                                {preset === TimeControlPreset.MARATHON && 'Marathon'}
                              </div>
                              <div className={styles.timeControlTime}>
                                {preset === TimeControlPreset.SPRINT && '1m +2s'}
                                {preset === TimeControlPreset.STANDARD && '3m +4s'}
                                {preset === TimeControlPreset.MARATHON && '10m +10s'}
                              </div>
                              {/* Show placement badge for competitive mode when not fully placed */}
                              {matchmakingTab === 'findMatch' && selectedMode === 'competitive' && !status.isPlaced && (
                                <div className={styles.placementBadge}>
                                  {status.games}/10
                                </div>
                              )}
                            </button>
                          );
                        })}

                        {/* "Any" button - only for Find Match AND only when ALL placements complete */}
                        {matchmakingTab === 'findMatch' && allPlacementsComplete() && (
                          <button
                            className={`${styles.timeControlButton} ${
                              selectedTimeControl === TimeControlPreset.ANY ? styles.timeControlButtonActive : ''
                            }`}
                            onClick={() => {
                              setSelectedTimeControl(TimeControlPreset.ANY);
                              localStorage.setItem('zodiac-time-control', TimeControlPreset.ANY);
                            }}
                          >
                            <div className={styles.timeControlName}>Any</div>
                            <div className={styles.timeControlTime}>Match any</div>
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Button - Start Search for Find Match */}
                  {matchmakingTab === 'findMatch' && (
                    <button
                      className={styles.searchButton}
                      onClick={handleStartSearch}
                      disabled={!user || !userProfile}
                    >
                      <span>{t('multiplayer.startSearch')}</span>
                    </button>
                  )}
                </>
              )
            ) : (
              <>
                <div className={styles.searchingHeader}>
                  <div className={styles.searchingAnimation}>
                    <div className={styles.spinner}></div>
                  </div>
                  <span className={styles.searchingText}>{t('multiplayer.searching')}</span>
                </div>

                <div className={styles.searchingInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoRowLabel}>{t('multiplayer.mode')}</span>
                    <span className={styles.infoRowValue}>
                      {getModeIcon(isInPlacementForSelectedTC() ? 'placement' : selectedMode)}{' '}
                      {isInPlacementForSelectedTC() ? t('multiplayer.placement') : t(`multiplayer.${selectedMode}`)}
                    </span>
                  </div>

                  <div className={styles.infoRow}>
                    <span className={styles.infoRowLabel}>{t('multiplayer.inQueue')}</span>
                    <span className={styles.infoRowValue}>
                      {searchStatus.playersInQueue} {searchStatus.playersInQueue !== 1 ? t('multiplayer.playersPlural') : t('multiplayer.players')}
                    </span>
                  </div>
                </div>

                <button
                  className={styles.cancelButton}
                  onClick={handleCancelSearch}
                >
                  <span>{t('multiplayer.cancelSearch')}</span>
                </button>
              </>
            )}
          </div>
        )}

        {/* Spectate Container - Only show when in matchmaking phase and not searching */}
        {gamePhase === 'matchmaking' && !searchStatus.searching && !isInPlacement && (
          <div className={styles.spectateContainer}>
            <button className={styles.spectateButton} onClick={handleOpenSpectate}>
              <span className={styles.spectateIcon}>👁</span>
              <span className={styles.spectateText}>Spectate</span>
            </button>
          </div>
        )}

        <div className={styles.gameInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('multiplayer.mode')}</span>
            <span className={styles.infoValue}>{t('multiplayer.title')}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>{t('multiplayer.gameType')}</span>
            <span className={styles.infoValue}>
              {isInPlacementForSelectedTC() ? t('multiplayer.placement') : t(`multiplayer.${selectedMode}`)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Time Control</span>
            <span className={styles.infoValue}>
              {/* Primary placement uses Marathon */}
              {isInPrimaryPlacement ? 'Marathon (10m +10s)' :
               selectedTimeControl === TimeControlPreset.SPRINT ? 'Sprint (1m +2s)' :
               selectedTimeControl === TimeControlPreset.MARATHON ? 'Marathon (10m +10s)' :
               'Standard (3m +4s)'}
            </span>
          </div>
          {(gameState.lightPlayer && gameState.darkPlayer && opponentUsername !== 'Unknown') && (
            <>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('multiplayer.opponent')}</span>
                <span className={styles.infoValue}>{opponentUsername}</span>
              </div>
              {/* Hide opponent ELO during placement matches and casual games */}
              {!isInPlacementForSelectedTC() && selectedMode !== 'casual' && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{t('multiplayer.opponentElo')}</span>
                  <span className={styles.infoValue}>
                    {opponentProfile?.mmr ? (() => {
                      const highest = MMRService.getHighestRating(opponentProfile as any);
                      return highest.isPlaced
                        ? highest.rating
                        : opponentProfile.mmr.placementGames
                        ? `${t('mainPage.placementProgress')} ${opponentProfile.mmr.placementGames}/10`
                        : t('multiplayer.unranked');
                    })() : t('multiplayer.unranked')}
                  </span>
                </div>
              )}
            </>
          )}
          {/* Color Balance debug - hidden, feature working well now */}
        </div>
      </div>

      {/* King Selection Modal or Waiting State */}
      {gamePhase === 'king-selection' && !isWaitingForOpponent && gameState.state === State.KingSelection && (
        <KingSelectionModal
          isLight={isSelectingForLight}
          playerType="Player (You)"
          onKingSelected={handleKingSelected}
        />
      )}

      {/* Waiting for Opponent Modal */}
      {gamePhase === 'king-selection' && isWaitingForOpponent && (
        <div className={styles.modalOverlay}>
          <div className={styles.waitingModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.waitingContent}>
              {showDisconnectNotice ? (
                <>
                  <div className={styles.waitingIconContainer}>
                    <span className={styles.disconnectIconLarge}>&#9888;</span>
                  </div>
                  <h2 className={styles.waitingTitle}>Opponent Left</h2>
                  <p className={styles.waitingMessage}>
                    {disconnectCountdown > 0
                      ? `Returning to lobby in ${disconnectCountdown}s`
                      : 'Returning to lobby...'}
                  </p>
                  <button
                    className={styles.waitingCancelButton}
                    onClick={handleCancelGame}
                  >
                    Return to Lobby
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.waitingIconContainer}>
                    <div className={styles.waitingPulse}></div>
                    <div className={styles.waitingIcon}></div>
                  </div>
                  <h2 className={styles.waitingTitle}>Awaiting Opponent</h2>
                  <p className={styles.waitingMessage}>{waitingMessage}</p>
                  <div className={styles.waitingDots}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <button
                    className={styles.waitingCancelButton}
                    onClick={handleCancelGame}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Warning Modal */}
      {warningMessage && (
        <div className={styles.modalOverlay}>
          <div className={styles.warningModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.warningContent}>
              <div className={styles.warningIconContainer}>
                <span className={styles.warningIconSymbol}>⚠️</span>
              </div>
              <h2 className={styles.warningTitle}>Invalid Placement</h2>
              <p className={styles.warningMessage}>{warningMessage}</p>
              <button
                className={styles.modalButton}
                onClick={() => setWarningMessage(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Game ID Copied Modal */}
      {copied && privateGameId && (
        <div className={styles.copiedModalOverlay} onClick={() => setCopied(false)}>
          <div className={styles.copiedModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.copiedModalIcon}>📋</div>
            <h3 className={styles.copiedModalTitle}>Game ID Copied!</h3>
            <p className={styles.copiedModalText}>Share this ID with your friend to start playing</p>
          </div>
        </div>
      )}

      {/* Post-Game Results Modal is now rendered inline in gameAreaContent for proper layout */}

      {/* Rematch Request Modal */}
      <RematchRequestModal
        isOpen={showRematchRequest}
        opponentUsername={opponentUsername}
        onAccept={handleAcceptRematch}
        onDecline={handleDeclineRematch}
      />

      {/* User Profile Modal */}
      {user && userProfile && (
        <UserProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          userData={null}
        />
      )}

      {/* Auth Modal for chat sign-in prompt */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="signUp"
      />

      {/* Game Area */}
      <div className={styles.gameArea}>
        <div className={styles.gameAreaContent}>
          {/* Chat box - Three-tier system based on account type and age */}
          {(gamePhase === 'playing' || showPostGameModal) && (() => {
            const chatEligibility = getChatEligibility(accountType, userProfile?.dateOfBirth);

            // Under 16 or real accounts without DOB: NO chat UI at all
            if (chatEligibility === 'none') {
              return null;
            }

            // Temp accounts: Show sign-in prompt (advertisement)
            if (chatEligibility === 'limited') {
              return (
                <div className={`${styles.chatPanel} ${showPostGameModal ? styles.chatPanelPostGame : ''}`}>
                  <h3 className={styles.chatTitle}>Chat</h3>
                  <div className={styles.chatSignInPrompt}>
                    <p className={styles.chatSignInMessage}>
                      Chat requires signing in
                    </p>
                    {opponentTriedToChat && (
                      <p className={styles.chatOpponentNotice}>
                        Your opponent is trying to chat with you. Sign in to see what they wrote!
                      </p>
                    )}
                    <button
                      className={styles.chatSignInButton}
                      onClick={() => setShowAuthModal(true)}
                    >
                      Sign In to Chat
                    </button>
                  </div>
                </div>
              );
            }

            // Full access (16+ real accounts): Check if user has chat disabled in settings
            if (!(userProfile?.chatEnabled ?? true)) {
              return null; // User chose to disable chat
            }

            // Original full chat UI
            return (
              <div className={`${styles.chatPanel} ${showPostGameModal ? styles.chatPanelPostGame : ''}`}>
                <h3 className={styles.chatTitle}>Chat</h3>
                {opponentProfile?.chatEnabled === false && (
                  <div className={styles.chatDisabledNotice}>
                    Opponent has chat disabled
                  </div>
                )}
                {/* Show note if opponent hasn't sent any messages yet and chat isn't disabled */}
                {opponentProfile?.chatEnabled !== false &&
                 !chatMessages.some(msg => msg.username !== userProfile?.username) && (
                  <div className={styles.chatDisabledNotice}>
                    Opponent hasn't opened chat yet
                  </div>
                )}
                <div className={styles.chatMessages}>
                  {chatMessages.map((msg, index) => (
                    <div key={index} className={styles.chatMessage}>
                      <strong>{msg.username}:</strong> {msg.text}
                    </div>
                  ))}
                </div>
                <form onSubmit={handleSendMessage} className={styles.chatForm}>
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Type a message..."
                    className={styles.chatInput}
                  />
                  <button type="submit" className={styles.chatSendButton}>Send</button>
                </form>
              </div>
            );
          })()}

          {/* Post-Game Modal - Render inline between chat and board when game ends */}
          {showPostGameModal && gameResult && (
            <>
              { }
              <PostGameModal
                isOpen={showPostGameModal}
                gameResult={gameResult}
                onClose={() => setShowPostGameModal(false)}
                onRematch={handleRequestRematch}
                onSearchAgain={handleSearchAgain}
                onBackToLobby={handleBackToLobby}
                isInline={true}
                winCondition={winCondition}
                myKing={gameState.lightPlayer === user?.uid ? gameState.lightKing : gameState.darkKing}
                opponentKing={gameState.lightPlayer === user?.uid ? gameState.darkKing : gameState.lightKing}
                isLightPlayer={gameState.lightPlayer === user?.uid}
                iHaveRequestedReveal={iHaveRequestedReveal}
                opponentRequestedReveal={opponentRequestedReveal}
                myRequestResponse={myRequestResponse}
                opponentRequestResponse={opponentRequestResponse}
                onRequestKingReveal={handleRequestKingReveal}
                onRespondToKingReveal={handleRespondToKingReveal}
              />
            </>
          )}

          {/* Center column with board and piece selection */}
          <div className={`${styles.centerColumn} ${showPostGameModal ? styles.centerColumnPostGame : ''}`}>
            {/* Opponent Disconnect Notification (delayed 10s to avoid flashing on brief hiccups) */}
            {/* Not shown for spectators - they don't need reconnect/forfeit options */}
            {showDisconnectNotice && (gamePhase === 'playing' || gamePhase === 'piece-placement') && !showPostGameModal && !isSpectating && (
              <div className={styles.disconnectNotice}>
                <span className={styles.disconnectIcon}>⚠</span>
                <div className={styles.disconnectContent}>
                  <span>Opponent has left the match</span>
                  <span className={styles.disconnectCountdown}>
                    {disconnectCountdown > 0
                      ? `Waiting for reconnect: ${disconnectCountdown}s`
                      : selectedMode === 'casual'
                        ? 'Opponent forfeited'
                        : 'Returning to lobby...'}
                  </span>
                  {selectedMode === 'casual' && (
                    <button
                      className={styles.disconnectSearchButton}
                      onClick={async () => {
                        // Mark the current game as ended (opponent forfeited) before searching again
                        // This prevents rejoining the same game
                        const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
                        const isLightPlayer = currentGameState.lightPlayer === user?.uid;

                        // Set lightWon based on who we are (we won since opponent forfeited)
                        currentGameState.lightWon = isLightPlayer;
                        currentGameState.state = State.PostGame;
                        (currentGameState as any).winCondition = 'forfeit';

                        // Send the game-ended update to Firebase
                        const eventManager = (ZodiacEngineService.zodiacEngine as any).eventManager;
                        if (eventManager) {
                          try {
                            await eventManager.sendUpdateToRemote(false);

                            // CRITICAL: Unsubscribe from old game's Firebase listener
                            // This prevents the old game from being picked up again
                            if (eventManager.unsubscribeDocument) {
                              eventManager.unsubscribeDocument();
                              eventManager.unsubscribeDocument = undefined;
                            }
                            // Clear the old gameId from EventManager
                            eventManager.gameId = undefined;
                          } catch (error) {
                          }
                        }

                        // Now reset local state for new search
                        setOpponentDisconnected(false);
                        setDisconnectCountdown(DISCONNECT_FORFEIT_SECONDS);
                        setGamePhase('matchmaking');
                        setGameId(null);
                        setContextGameId(null);

                        // Reset game state
                        const gameStateManager = (ZodiacEngineService.zodiacEngine as any).gameStateManager;
                        gameStateManager.emptyBoard();
                        gameStateManager.setMode(Mode.Live);
                        gameStateManager.setGameStateState(State.KingSelection);
                        const freshGameState = gameStateManager.getGameState();
                        freshGameState.fenHistory = ['8/8/8/8/8/8/8/8'];
                        freshGameState.lightKing = null;
                        freshGameState.darkKing = null;
                        freshGameState.lightWon = undefined;
                        freshGameState.isLightTurn = true;
                        freshGameState.lightPlayer = undefined;
                        freshGameState.darkPlayer = undefined;
                        updateGameState(freshGameState);
                      }}
                    >
                      Search for New Game
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Timer for opponent (top) */}
            {gamePhase === 'playing' && user && (
              <TimerDisplay
                player={gameState.lightPlayer === user.uid ? 'dark' : 'light'}
                isActive={gameState.isLightTurn === (gameState.lightPlayer !== user.uid)}
              />
            )}

            <div className={styles.boardContainer}>
              <Board
                gameState={gameState}
                uiState={{
                  potentialMoves: potentialMoves,
                  potentialSideEffects: potentialSideEffects,
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                highlightedRows={gamePhase === 'piece-placement' && user ? (() => {
                  const isLightPlayer = gameState.lightPlayer === user.uid;
                  return isLightPlayer ? { light: 0, dark: null } : { light: null, dark: 7 }; // Show only player's row
                })() : undefined}
                flipBoard={user ? gameState.lightPlayer === user.uid : true}
              />
            </div>

            {/* Timer for current player (bottom) */}
            {gamePhase === 'playing' && user && (
              <TimerDisplay
                player={gameState.lightPlayer === user.uid ? 'light' : 'dark'}
                isActive={gameState.isLightTurn === (gameState.lightPlayer === user.uid)}
              />
            )}

            {/* Piece Selection Panel - Show underneath the board during piece placement */}
            {gamePhase === 'piece-placement' && (
              <div className={styles.pieceSelectionPanel}>
                {renderPieceSelection()}
              </div>
            )}
          </div>

          {/* Right side panels with move history and game info - Hide when post-game modal shows */}
          {gamePhase === 'playing' && !showPostGameModal && (
            <div className={styles.rightPanel}>
              <FENHistory ref={fenHistoryRef} />
              <GameInfo />
              {/* Show King Button - highlights player's king for 3 seconds */}
              {user && !isSpectating && (
                <button
                  className={`${styles.showKingButton} ${kingHighlightActive ? styles.showKingButtonActive : ''}`}
                  onClick={activateKingHighlight}
                  disabled={kingHighlightActive}
                >
                  {kingHighlightActive ? 'KING HIGHLIGHTED' : 'SHOW KING'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Reconnect Modal */}
      {showReconnectModal && activeGameToReconnect && (
        <div className={styles.modalOverlay} onClick={handleDismissReconnect}>
          <div className={styles.reconnectModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.reconnectIconContainer}>
              <span className={styles.reconnectIconSymbol}>&#x21BA;</span>
            </div>
            <h2 className={styles.reconnectTitle}>Game In Progress</h2>
            <p className={styles.reconnectMessage}>
              You have an active game waiting for you. Would you like to reconnect?
            </p>
            <div className={styles.reconnectButtons}>
              <button
                className={styles.reconnectButton}
                onClick={handleReconnect}
                disabled={isReconnecting}
              >
                {isReconnecting ? 'Reconnecting...' : 'Reconnect'}
              </button>
              <button
                className={styles.forfeitButton}
                onClick={handleDismissReconnect}
                disabled={isReconnecting}
              >
                Forfeit Game
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spectate Modal */}
      <SpectateModal
        isOpen={showSpectateModal}
        onClose={() => setShowSpectateModal(false)}
        onSelectGame={handleStartSpectating}
      />
    </AdSenseLayout>
  );
}

export default Multiplayer;

